// ThankYouPage.js — ALWAYS VISIBLE type chooser with clear copy
// Types: released | planned | review_only

import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import './ThankYouPage.css';
import { getSpotifyArtistAndTitle, isSpotifyUrl } from './spotifyAutoFill';
import OrderVerificationLoader from './components/OrderVerificationLoader';
import { applyRandomFlicker, decodeTextLocked, runAfterFontsReady } from './utils/textEffects';
import { trackSiteEvent } from './siteAnalytics';
import { useRegionalVisibility } from './regionalVisibility';

const RAW_API_BASE =
  process.env.REACT_APP_BACKEND_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'http://localhost:4000';

const API_BASE_URL = normalizeApiBase(RAW_API_BASE);
const PRIMARY_PROXY_BASE = '/api/proxy?url=';
const FALLBACK_PROXY_BASE = 'https://api.allorigins.win/raw?url=';
const R_JINA_PROXY_BASE = 'https://r.jina.ai/';
const SUBMIT_ENDPOINT = buildApiUrl('/submit-track');
const ORDER_STATUS_ENDPOINT = buildApiUrl('/orders/status');
const SHARED_TRACKS_ENDPOINT = buildApiUrl('/shared-tracks');
const SPOTIFY_RELEASE_VALIDATION_ENDPOINT = buildApiUrl('/spotify/validate-release-link');
const LINK_META_ENDPOINT = buildApiUrl('/link-meta');
const QUICK_CHECK_DUPLICATE_MESSAGE = 'This track was already shared for your account. Please submit it as Standard or Premium instead of Quick Check.';
const COOLDOWN_BASE_MESSAGE = 'This track was already shared recently.';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const SUPPORT_EMAIL = 'support@ivagonzo.com';
const ATTN_RED = 'ivg-attn ivg-brackets ivg-burst ivg-c-red ivg-block';
const ATTN_BLUE = 'ivg-attn ivg-brackets ivg-burst ivg-c-blue ivg-block';
const ORDER_STATUS_POLL_TIMEOUT_MS = 5 * 60 * 1000;
const ORDER_STATUS_DEFAULT_RETRY_MS = 1500;
const VERIFY_FAST_WINDOW_MS = 20 * 1000;
const MIN_VERIFY_GATE_MS = 2 * 1000;
const PORTAL_ONLY_THANK_YOU = true;
const ORDER_TIMEOUT_MESSAGE = "We're still waiting for confirmation. Please refresh in a moment or contact support.";
const RELEASED_SPOTIFY_LINK_MESSAGE =
  'Please provide a Spotify track link, or an album link only if it is a 1-track single.';
const RELEASED_SPOTIFY_CHECKING_MESSAGE = 'Checking whether this Spotify release is a 1-track single…';
const RELEASED_SPOTIFY_UNVERIFIED_MESSAGE =
  'We couldn’t verify this Spotify album link. Use the direct track link, or only use album links for 1-track singles.';
const SUPPORTED_RELEASE_LINK_MESSAGE =
  'Use a direct track link, or an album/playlist link only if it contains exactly 1 track.';
const SUPPORTED_RELEASE_CHECKING_MESSAGE = 'Checking whether this release link is a 1-track single…';
const SUPPORTED_RELEASE_UNVERIFIED_MESSAGE =
  'We couldn’t verify this release link. Use the direct track link, or only use album/playlist links for 1-track singles.';
const NOTES_LINK_NOT_ALLOWED_MESSAGE =
  'Links are not allowed in notes. Put the track link in the track field instead.';
const SUBMISSION_NOTES_MAX = 2000;
const SUBMISSION_NOTES_LIMIT_MESSAGE = `You reached the ${SUBMISSION_NOTES_MAX.toLocaleString()} character limit.`;
const SUBMISSION_AUDIO_ACCEPT = '.mp3,.wav,.flac,.aif,.aiff,.aac,.m4a,.ogg';
const SUBMISSION_AUDIO_LABEL = 'mp3, wav, flac, aif, aiff, aac, m4a, ogg';
const SUBMISSION_AUDIO_HINT = `Supported formats: ${SUBMISSION_AUDIO_LABEL}. Max 100MB.`;
const UNSUPPORTED_SUBMISSION_FILE_MESSAGE =
  `Unsupported file format. Supported formats: ${SUBMISSION_AUDIO_LABEL}.`;

function normalizeApiBase(base) {
  if (!base) return '';
  const trimmed = String(base).trim();
  if (trimmed === '/' || trimmed === '/api') return '/api';
  return trimmed.replace(/\/$/, '');
}

function buildApiUrl(path) {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  if (!API_BASE_URL) {
    return `/api${cleanPath}`.replace(/\/api\/+/, '/api/');
  }

  if (API_BASE_URL === '/api') {
    return `${API_BASE_URL}${cleanPath}`.replace(/\/api\/+/, '/api/');
  }

  const normalizedBase = API_BASE_URL.replace(/\/$/, '');
  if (/\/api$/i.test(normalizedBase)) {
    return `${normalizedBase}${cleanPath}`.replace(/\/api\/+/, '/api/');
  }

  const joined = `${normalizedBase}/api${cleanPath}`;
  return joined.replace(/\/api\/+/, '/api/');
}

function hasSupportedSubmissionAudioExtension(name) {
  return /\.(mp3|wav|flac|aiff?|aac|m4a|ogg)$/i.test(String(name || '').trim());
}

function isSupportedSubmissionAudioFile(file) {
  if (!file) return false;
  const type = String(file.type || '').toLowerCase();
  const mimeOk = !type || type.startsWith('audio/') || type === 'application/octet-stream';
  return mimeOk && hasSupportedSubmissionAudioExtension(file.name || '');
}

/* ================= helpers ================= */

function isCanonicalInternalOrderId(value) {
  return /^ord_[a-f0-9]{32}$/i.test(String(value || '').trim());
}

function getQuery() {
  const params = new URLSearchParams(window.location.search);
  const suppliedOrderId = params.get('order_id') || '';
  const canonicalOrderId = isCanonicalInternalOrderId(suppliedOrderId) ? suppliedOrderId : '';

  // Older receipts used `order`, `orderId` and sometimes `session_id`. They
  // are only a signal to offer the generic client-portal route: do not send
  // any legacy value to order-status or bind it to a protected purchase context.
  const hasLegacyOrderLink = Boolean(
    !canonicalOrderId && (suppliedOrderId || params.get('order') || params.get('orderId'))
  );

  return {
    orderId: canonicalOrderId,
    hasLegacyOrderLink,
    email: params.get('email') || '',
    productId: params.get('product_id') || '',
    plan: params.get('plan') || '',
    shaSign: params.get('sha_sign') || '',
  };
}

function buildClientSubmitPath(orderId) {
  const params = new URLSearchParams();
  const safeOrderId = String(orderId || '').trim();
  params.set('status', 'NEW');
  params.set('open', 'submit');
  if (safeOrderId) params.set('order_id', safeOrderId);
  return `/client?${params.toString()}`;
}

function autoGrow(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
}

function normalizePlanName(plan) {
  const s = String(plan || '').trim().toLowerCase();
  if (!s) return '';
  if (s.includes('quick')) return 'quick_check';
  if (s.includes('prem')) return 'premium';
  if (s.includes('standart') || s.includes('standard') || s === 'std') return 'standard';
  return s;
}

const REVIEW_STYLE_OPTIONS = [
  {
    value: 'supportive',
    label: 'Gentle',
    toneClass: 'thankyou-review-option--supportive',
    description: 'Mostly light, encouraging, and focused on what already works. Critique stays minimal.',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    toneClass: 'thankyou-review-option--balanced',
    description: 'A clear read on the strong parts, weak spots, and what could hit harder.',
  },
  {
    value: 'no_filter',
    label: 'No Filter',
    toneClass: 'thankyou-review-option--no-filter',
    description: 'The deepest cut: direct critique, no sugar coating, with specific ideas to sharpen the track.',
  },
];

function normalizeReviewStyleValue(value) {
  const style = String(value || '').trim().toLowerCase();
  if (style === 'supportive') return 'supportive';
  if (style === 'no_filter' || style === 'nofilter' || style === 'no-filter') return 'no_filter';
  return 'balanced';
}

function getReviewStyleForPlan(plan, value) {
  const normalizedPlan = normalizePlanName(plan);
  if (normalizedPlan === 'quick_check') return '';
  const style = normalizeReviewStyleValue(value);
  if (style === 'no_filter' && normalizedPlan !== 'premium') return 'balanced';
  return style;
}

function spotifyTrackKey(url) {
  if (!url) return null;
  try {
    const raw = String(url).trim();
    if (!raw) return null;
    const u = new URL(raw);
    const host = u.hostname.replace(/^www\./i, '').toLowerCase();
    if (host !== 'open.spotify.com') return null;
    const path = u.pathname.replace(/^\/intl-[a-z-]+/i, '');
    const match = path.match(/^\/(track)\/([A-Za-z0-9]+)(?:\/|$)/);
    if (!match) return null;
    return `${host}/${match[1]}/${match[2]}`;
  } catch {
    return null;
  }
}

function canonicalLinkKey(url) {
  const spotifyKey = spotifyTrackKey(url);
  if (spotifyKey) return `spotify:${spotifyKey}`;
  const youtubeKey = youtubeVideoKey(url);
  if (youtubeKey) return `youtube:${youtubeKey}`;
  const appleKey = appleTrackKey(url);
  if (appleKey) return `apple:${appleKey}`;
  if (!url) return null;
  const raw = String(url).trim();
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    parsed.hash = '';
    parsed.search = '';
    const normalized = `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, '').toLowerCase();
    return normalized ? `raw:${normalized}` : null;
  } catch {
    const normalized = raw.replace(/[?#].*$/, '').replace(/\/+$/, '').toLowerCase();
    return normalized ? `raw:${normalized}` : null;
  }
}

function youtubeVideoKey(url) {
  if (!url) return null;
  try {
    const raw = String(url).trim();
    if (!raw) return null;
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    if (host === 'youtu.be') {
      const id = parsed.pathname.split('/').filter(Boolean)[0] || '';
      return /^[A-Za-z0-9_-]{6,}$/.test(id) ? id : null;
    }
    if (host !== 'youtube.com' && host !== 'm.youtube.com' && host !== 'music.youtube.com') {
      return null;
    }
    if (parsed.pathname.replace(/\/+$/, '') !== '/watch') return null;
    const videoId = String(parsed.searchParams.get('v') || '').trim();
    return /^[A-Za-z0-9_-]{6,}$/.test(videoId) ? videoId : null;
  } catch {
    return null;
  }
}

function appleTrackKey(url) {
  if (!url) return null;
  try {
    const raw = String(url).trim();
    if (!raw) return null;
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    if (host !== 'music.apple.com' && !host.endsWith('.music.apple.com')) return null;
    const trackId = String(parsed.searchParams.get('i') || '').trim();
    if (/^\d+$/.test(trackId)) return trackId;
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (String(parts[1] || '').toLowerCase() !== 'song') return null;
    const songId = String(parts[parts.length - 1] || '').trim();
    return /^\d+$/.test(songId) ? songId : null;
  } catch {
    return null;
  }
}

function isAppleMusicAutofillUrl(url) {
  if (!url) return false;
  try {
    const parsed = new URL(String(url).trim());
    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    return host === 'music.apple.com' || host.endsWith('.music.apple.com');
  } catch {
    return false;
  }
}

function isYouTubeMusicAutofillUrl(url) {
  if (!url) return false;
  try {
    const parsed = new URL(String(url).trim());
    return parsed.hostname.replace(/^www\./i, '').toLowerCase() === 'music.youtube.com';
  } catch {
    return false;
  }
}

async function fetchRichLinkMeta(url) {
  if (!url) return null;
  try {
    const endpoint = LINK_META_ENDPOINT.startsWith('http://') || LINK_META_ENDPOINT.startsWith('https://')
      ? new URL(LINK_META_ENDPOINT)
      : new URL(LINK_META_ENDPOINT, window.location.origin);
    endpoint.searchParams.set('url', String(url).trim());
    const resp = await fetch(endpoint.toString(), {
      headers: { Accept: 'application/json' },
    });
    if (!resp.ok) return null;
    const data = await resp.json().catch(() => null);
    return data && data.ok ? data : null;
  } catch {
    return null;
  }
}

function normalizeComboKey(artist, song) {
  const a = String(artist || '').trim().toLowerCase();
  const s = String(song || '').trim().toLowerCase();
  if (!a || !s) return null;
  return `${a}:::${s}`;
}

function InlineProgress({ message, note }) {
  return (
    <div className="ivg-progress">
      <div className="ivg-progress__bar" />
      <div className="ivg-progress__text">
        <div>
          <span className="ivg-progress__icon">
            <ProgressIcon />
          </span>
          <strong>{message}</strong>
        </div>
        {note ? <div className="ivg-progress__note">{note}</div> : null}
      </div>
    </div>
  );
}

function ProgressIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm1 5v5.59l3.3 3.3-1.42 1.42-3.88-3.88V7Z"
        fill="currentColor"
      />
    </svg>
  );
}

/* ---- robust proxy helpers (cache-busting + retries) ---- */
function addNoCacheParam(u) {
  try {
    const urlObj = new URL(u);
    urlObj.searchParams.set('_', Date.now().toString());
    return urlObj.toString();
  } catch {
    return u + (u.includes('?') ? '&' : '?') + '_=' + Date.now();
  }
}

function buildProxyChain(base = PRIMARY_PROXY_BASE) {
  const chain = [];
  const seen = new Set();
  const push = (entry) => {
    const key = `${entry.mode}:${entry.base}`;
    if (seen.has(key)) return;
    seen.add(key);
    chain.push(entry);
  };
  const normalized = String(base || '').trim();
  if (normalized) push({ base: normalized, mode: 'encode' });
  push({ base: FALLBACK_PROXY_BASE, mode: 'encode' });
  push({ base: R_JINA_PROXY_BASE, mode: 'passthrough' });
  return chain;
}

function formatProxyUrl(entry, url) {
  if (entry.mode === 'passthrough') {
    return entry.base.endsWith('/') ? `${entry.base}${url}` : `${entry.base}/${url}`;
  }
  return `${entry.base}${encodeURIComponent(url)}`;
}

async function proxiedFetchText(base = PRIMARY_PROXY_BASE, targetUrl, attempts = 2) {
  const proxies = buildProxyChain(base);
  let lastErr;
  for (const entry of proxies) {
    for (let i = 0; i < attempts; i++) {
      try {
        const t = addNoCacheParam(targetUrl);
        const finalUrl = formatProxyUrl(entry, t);
        const res = await fetch(finalUrl, { redirect: 'follow' });
        if (res.ok) {
          return await res.text();
        }
        lastErr = new Error('HTTP ' + res.status);
      } catch (e) {
        lastErr = e;
      }
      await new Promise((r) => setTimeout(r, i * 150 + 100));
    }
  }
  throw lastErr || new Error('proxy text fetch failed');
}

async function proxiedFetchJSON(base = PRIMARY_PROXY_BASE, targetUrl, attempts = 2) {
  const txt = await proxiedFetchText(base, targetUrl, attempts);
  try {
    return JSON.parse(txt);
  } catch (e) {
    throw new Error('Invalid JSON from proxy');
  }
}

function detectStrictMusicLinkProvider(url) {
  if (!url) return '';
  try {
    const parsed = new URL(String(url).trim());
    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    if (host === 'open.spotify.com') return 'spotify';
    if (host === 'soundcloud.com' || host === 'on.soundcloud.com') return 'soundcloud';
    if (host === 'music.apple.com' || host.endsWith('.music.apple.com')) return 'apple_music';
    if (host === 'youtu.be' || host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
      return 'youtube';
    }
    return '';
  } catch {
    return '';
  }
}

function makeReleasedLinkCheck(url, data = {}) {
  if (data && data.ok) {
    return {
      url,
      status: 'valid',
      message: '',
      code: String(data.code || ''),
      normalizedUrl: String(data.normalized_url || url || ''),
      canonicalUrl: String(data.canonical_url || data.normalized_url || url || ''),
    };
  }

  return {
    url,
    status: 'invalid',
    message: String(data.error || SUPPORTED_RELEASE_LINK_MESSAGE),
    code: String(data.code || 'LINK_INVALID'),
    normalizedUrl: String(data.normalized_url || ''),
    canonicalUrl: String(data.canonical_url || ''),
  };
}

function makeReleasedLinkChecking(url) {
  return {
    url,
    status: 'checking',
    message: SUPPORTED_RELEASE_CHECKING_MESSAGE,
    code: '',
    normalizedUrl: '',
    canonicalUrl: '',
  };
}

function makeReleasedLinkUnverified(url) {
  const provider = detectStrictMusicLinkProvider(url);
  return {
    url,
    status: 'invalid',
    message: provider === 'spotify'
      ? RELEASED_SPOTIFY_UNVERIFIED_MESSAGE
      : SUPPORTED_RELEASE_UNVERIFIED_MESSAGE,
    code: 'LINK_UNVERIFIED',
    normalizedUrl: '',
    canonicalUrl: '',
  };
}

async function fetchReleasedSpotifyLinkCheck(url, signal) {
  const resp = await fetch(
    `${SPOTIFY_RELEASE_VALIDATION_ENDPOINT}?url=${encodeURIComponent(url)}`,
    {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      ...(signal ? { signal } : {}),
    }
  );
  const data = await resp.json().catch(() => ({}));
  return makeReleasedLinkCheck(url, data);
}

function isValidHttpUrl(url) {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

function containsForbiddenNotesLink(value) {
  return /(?:https?:\/\/|www\.)\S+/i.test(String(value || ''));
}

/* today YYYY-MM-DD for min date */
function todayISO() {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d - tzOffset).toISOString().slice(0, 10);
}

/* ===== title parsing helpers (module-scope) ===== */
function cleanField(s = '') {
  return String(s).replace(/\s+/g, ' ').trim();
}
function stripTitleNoise(t = '') {
  return String(t)
    // удаляем круглые/квадратные вставки типа (Official Video) [Lyrics] и т.д.
    .replace(/\((?:official|video|lyrics?|audio|visualizer|prod\.[^)]*|HD|4K)[^)]*\)/gi, '')
    .replace(/\[[^\]]+\]/g, '')
    // популярный мусор в хвосте
    .replace(/official\s+music\s*video/gi, '')
    .replace(/lyric\s*video/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}
function splitArtistSong(rawTitle = '') {
  const t0 = stripTitleNoise(rawTitle);
  const t = stripProviderSuffix(t0);
  const clean = (s) => String(s).replace(/\s+/g, ' ').trim();

  // Вариант: “Song” by Artist
  const by = t.match(/^\s*["“]?(.+?)["”]?\s+by\s+(.+)\s*$/i);
  if (by) {
    return {
      artist: stripProviderSuffix(clean(by[2])),
      song: clean(by[1]),
    };
  }

  // Разделители -, –, —, •, ·, |
  const parts = t.split(/\s+[-–—•·|]\s+/);
  if (parts.length >= 2) {
    const left = clean(parts[0]);
    const rightRaw = clean(parts.slice(1).join(' - '));
    const right = stripProviderSuffix(rightRaw);

    // Если это "Song - <модификатор>" или "<модификатор> - Song", НЕ делим
    if (parts.length === 2 && (isModifierPhrase(right) || isModifierPhrase(left))) {
      return { artist: '', song: clean(`${left} - ${right}`) };
    }

    // По умолчанию — как раньше: лево = артист, право = песня
    return { artist: left, song: clean(right) };
  }

  return { artist: '', song: clean(t) };
}

// Убирает хвосты вида " - Spotify", " | Spotify", " · Spotify", " — Spotify", а также " - on Spotify"
function stripProviderSuffix(s = '') {
  return String(s)
    .replace(/\s*[-–—|·•]\s*(?:on\s+)?spotify\s*$/i, '')
    .trim();
}

// Ровно распознаём "модификаторные" приписки (это НЕ артист)
function isModifierPhrase(s = '') {
  const v = String(s).trim();
  if (!v) return false;

  // Базовые ключевые слова и варианты
  const base =
    /\b(remix|mix|edit|version|remaster|vip|bootleg|rework|radio edit|extended|instrumental|demo|live|acoustic)\b/i;

  if (base.test(v)) return true;
  if (/\bmix\s*$/i.test(v)) return true;                       // "... Mix" на конце
  if (/\bslowed\b/i.test(v)) return true;                      // "SLOWED ..."
  if (/\bsped\s*up\b/i.test(v)) return true;                   // "sped up"
  if (/\brever(b|d)\b/i.test(v)) return true;                  // "reverb" / "reverd" (опечатки)
  if (/\bambient\b/i.test(v)) return true;                     // "Ambient"
  if (/\blo[-\s]*fi\b/i.test(v)) return true;                  // "lofi", "lo-fi"
  if (/\bclub\s*mix\b/i.test(v)) return true;                  // "Club Mix"

  return false;
}

/* ================= component ================= */

export default function ThankYouPage() {
  const { hideInstagram } = useRegionalVisibility();
  const initialQuery = useMemo(() => getQuery(), []);
  const [q, setQ] = useState(initialQuery);

  const [artist, setArtist] = useState('');
  const [songName, setSongName] = useState('');
  const [artistDirty, setArtistDirty] = useState(false);
  const [songDirty, setSongDirty] = useState(false);

  const [requestType, setRequestType] = useState('released'); // released | planned | review_only
  const [plannedDate, setPlannedDate] = useState('');

  const [track, setTrack] = useState('');
  const [reviewStyle, setReviewStyle] = useState('balanced');
  const [notes, setNotes] = useState('');
  const [agree, setAgree] = useState(false);
  const [file, setFile] = useState(null);
  const [fileErrorMessage, setFileErrorMessage] = useState('');
  const [sharedSpotifyKeys, setSharedSpotifyKeys] = useState([]);
  const [sharedSpotifyRecords, setSharedSpotifyRecords] = useState({});
  const [pendingRecords, setPendingRecords] = useState({});
  const [pendingCombos, setPendingCombos] = useState({});
  const [quickCheckWarning, setQuickCheckWarning] = useState('');
  const [autofillStatus, setAutofillStatus] = useState('idle');
  const [autofillMessage, setAutofillMessage] = useState('');
  const [autofillLocked, setAutofillLocked] = useState(false);
  const [releasedLinkCheck, setReleasedLinkCheck] = useState(() => makeReleasedLinkCheck('', { ok: false, error: '', code: '' }));

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showRefund, setShowRefund] = useState(false);


  const [orderInfo, setOrderInfo] = useState(null);
  const [orderStatusError, setOrderStatusError] = useState('');
  const [orderStatusChecked, setOrderStatusChecked] = useState(false);
  const [orderCanSubmit, setOrderCanSubmit] = useState(false);
  const [submissionAuthorized, setSubmissionAuthorized] = useState(false);
  const [orderVerificationState, setOrderVerificationState] = useState('idle');
  const [verifyDelayReached, setVerifyDelayReached] = useState(false);
  const [verifyGateHoldActive, setVerifyGateHoldActive] = useState(false);

  const titleRef = useRef(null);
  const notesRef = useRef(null);
  const lastLookupRef = useRef({ url: '', at: 0 });
  const autofillTimerRef = useRef(null);
  const lastAutofillRef = useRef({ url: '', artist: '', song: '' });
  const autofillLockTimerRef = useRef(null);
  const pendingAutofillUrlRef = useRef('');
  const releasedLinkCacheRef = useRef(new Map());
  const verifyGateShownAtRef = useRef(0);
  const verifyGateHoldTimerRef = useRef(null);
  const analyticsThankYouTrackedRef = useRef(false);
  const analyticsOrderVerifiedRef = useRef(false);

  const planSource = orderInfo?.plan_key || orderInfo?.plan || q.plan;
  const planNormalized = useMemo(() => normalizePlanName(planSource), [planSource]);
  const emailNormalized = useMemo(() => (q.email || '').trim().toLowerCase(), [q.email]);
  const trimmedTrack = useMemo(() => track.trim(), [track]);
  const strictMusicProvider = useMemo(
    () => detectStrictMusicLinkProvider(trimmedTrack),
    [trimmedTrack]
  );
  const strictMusicLinkEntered = useMemo(() => Boolean(strictMusicProvider), [strictMusicProvider]);
  const isQuickCheckPlan = planNormalized === 'quick_check';
  const reviewStyleSubmitValue = useMemo(
    () => getReviewStyleForPlan(planNormalized, reviewStyle),
    [planNormalized, reviewStyle]
  );
  const reviewStyleDisplayValue = reviewStyleSubmitValue || 'balanced';
  const tracksTotal = Number(orderInfo?.tracks_total) || 0;
  const tracksUsed = Number(orderInfo?.tracks_used) || 0;
  const tracksRemaining = Math.max(0, tracksTotal - tracksUsed);
  const orderAllowsSubmission = Boolean(orderInfo) && orderCanSubmit && submissionAuthorized;
  const orderPlanKey = orderInfo?.plan_key || planSource || '';
  const showEmailAuthorizationGate =
    !submitted && orderVerificationState === 'authorization_required';
  const canonicalInternalOrderId = String(
    orderInfo?.internal_order_id || orderInfo?.order_id || ''
  ).trim();
  const hasCanonicalInternalOrderId = isCanonicalInternalOrderId(canonicalInternalOrderId);
  const genericClientPortalPath = '/client?status=NEW';
  const clientSubmitPath = useMemo(
    () => buildClientSubmitPath(canonicalInternalOrderId),
    [canonicalInternalOrderId]
  );
  const requestLoginTarget = hasCanonicalInternalOrderId
    ? clientSubmitPath
    : genericClientPortalPath;
  const requestLoginLink = `/client/login?next=${encodeURIComponent(requestLoginTarget)}`;
  const portalPlanLabel = planNormalized === 'premium'
    ? 'Premium'
    : planNormalized === 'quick_check'
      ? 'Quick Check'
      : planNormalized === 'standard'
        ? 'Standard'
        : 'Submission';
  const gateTimedOut = orderVerificationState === 'failed' && (orderStatusError === ORDER_TIMEOUT_MESSAGE || orderStatusError === '');
  const showVerificationGate =
    !submitted && (
      orderVerificationState === 'verifying' ||
      (orderVerificationState === 'idle' && Boolean(q.orderId)) ||
      gateTimedOut ||
      verifyGateHoldActive
    );
  const verifyTextSegments = useMemo(
    () => [
      { text: 'Confirming your payment. Please wait a moment while we verify it.' },
    ],
    [],
  );
  const verifyDelaySegments = useMemo(
    () => [
      {
        text:
          'This is taking longer than usual. You\'ll receive a confirmation email shortly — please check Spam/Promotions. If nothing arrives within 1 hour, contact ',
      },
      { text: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}`, target: '_blank', rel: 'noreferrer' },
      { text: " and we'll help manually." },
    ],
    [],
  );

  useEffect(() => {
    if (analyticsThankYouTrackedRef.current) return;
    analyticsThankYouTrackedRef.current = true;
    trackSiteEvent('thank_you_viewed', {
      source_page: 'thank_you',
      order_id: q.orderId || '',
      plan_key: q.plan || '',
    });
  }, [q.orderId, q.plan]);

  useEffect(() => {
    if (analyticsOrderVerifiedRef.current || orderVerificationState !== 'verified' || !orderInfo) {
      return;
    }
    analyticsOrderVerifiedRef.current = true;
    trackSiteEvent('order_verified', {
      source_page: 'thank_you',
      order_id: q.orderId || orderInfo.order_id || orderInfo.id || '',
      plan_key: orderInfo.plan_key || orderInfo.plan || q.plan || '',
      tracks_total: Number(orderInfo.tracks_total) || '',
      tracks_used: Number(orderInfo.tracks_used) || '',
    });
  }, [orderVerificationState, orderInfo, q.orderId, q.plan]);
  const sharedSpotifyKeySet = useMemo(() => new Set(sharedSpotifyKeys), [sharedSpotifyKeys]);
  const currentReleasedLinkCheck = useMemo(
    () => (releasedLinkCheck.url === trimmedTrack ? releasedLinkCheck : null),
    [releasedLinkCheck, trimmedTrack]
  );
  const resolvedTrackUrl = useMemo(() => {
    if (currentReleasedLinkCheck?.status === 'valid') {
      return currentReleasedLinkCheck.canonicalUrl || currentReleasedLinkCheck.normalizedUrl || trimmedTrack;
    }
    return trimmedTrack;
  }, [currentReleasedLinkCheck, trimmedTrack]);
  const pendingDuplicateInfo = useMemo(() => {
    const linkKey = canonicalLinkKey(resolvedTrackUrl);
    if (linkKey) {
      const linkStatus = pendingRecords[linkKey];
      if (linkStatus) {
        return {
          blocked: true,
          message: linkStatus === 'APPROVED'
            ? 'This track link is already approved and waiting to be shared.'
            : 'This track link was already submitted and is awaiting review.',
        };
      }
    }
    const comboKey = normalizeComboKey(artist, songName);
    if (comboKey) {
      const comboStatus = pendingCombos[comboKey];
      if (comboStatus) {
        return {
          blocked: true,
          message: comboStatus === 'APPROVED'
            ? 'This artist and song are already approved and waiting to be shared.'
            : 'This artist and song were already submitted and are awaiting review.',
        };
      }
    }
    return { blocked: false, message: '' };
  }, [resolvedTrackUrl, artist, songName, pendingRecords, pendingCombos]);
  const pendingDuplicateMessage = pendingDuplicateInfo.message;
  const hasPendingDuplicate = pendingDuplicateInfo.blocked;
  const thankYouFormThemeClass = useMemo(() => {
    if (planNormalized === 'premium') return 'track-form--premium';
    if (planNormalized === 'quick_check') return 'track-form--quick_check';
    return 'track-form--standard';
  }, [planNormalized]);
  const thankYouPageThemeClass = useMemo(() => {
    if (planNormalized === 'premium') return 'thankyou-theme--premium';
    if (planNormalized === 'quick_check') return 'thankyou-theme--quick_check';
    return 'thankyou-theme--standard';
  }, [planNormalized]);

  useEffect(() => {
    if (planNormalized !== 'premium' && normalizeReviewStyleValue(reviewStyle) === 'no_filter') {
      setReviewStyle('balanced');
    }
  }, [planNormalized, reviewStyle]);

  const handleReviewStyleChange = useCallback((value) => {
    if (isQuickCheckPlan) return;
    const normalized = normalizeReviewStyleValue(value);
    if (normalized === 'no_filter' && planNormalized !== 'premium') return;
    setReviewStyle(normalized);
  }, [isQuickCheckPlan, planNormalized]);

  const runReleasedLinkValidation = useCallback(async (url, { signal } = {}) => {
    const raw = String(url || '').trim();
    if (!raw) {
      return makeReleasedLinkCheck('', { ok: false, error: '', code: '' });
    }
    const cached = releasedLinkCacheRef.current.get(raw);
    if (cached) return cached;
    const result = await fetchReleasedSpotifyLinkCheck(raw, signal);
    if (result.status === 'valid' || result.code !== 'LINK_UNVERIFIED') {
      releasedLinkCacheRef.current.set(raw, result);
    }
    return result;
  }, []);

  useEffect(() => {
    const shouldValidateStrictLink =
      strictMusicLinkEntered && (requestType !== 'released' || strictMusicProvider === 'spotify');
    if (!trimmedTrack || !shouldValidateStrictLink) {
      setReleasedLinkCheck(makeReleasedLinkCheck('', { ok: false, error: '', code: '' }));
      return undefined;
    }

    const cached = releasedLinkCacheRef.current.get(trimmedTrack);
    if (cached) {
      setReleasedLinkCheck(cached);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      setReleasedLinkCheck(makeReleasedLinkChecking(trimmedTrack));
      runReleasedLinkValidation(trimmedTrack, { signal: controller.signal })
        .then((result) => {
          if (controller.signal.aborted) return;
          setReleasedLinkCheck(result);
        })
        .catch((err) => {
          if (controller.signal.aborted || err?.name === 'AbortError') return;
          setReleasedLinkCheck(makeReleasedLinkUnverified(trimmedTrack));
        });
    }, 220);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [trimmedTrack, strictMusicLinkEntered, strictMusicProvider, requestType, runReleasedLinkValidation]);

  useEffect(() => {
    const orderId = (q.orderId || '').trim();
    const email = (q.email || '').trim();
    const productId = (q.productId || '').trim();
    const shaSign = (q.shaSign || '').trim();

    if (!orderId) {
      setOrderInfo(null);
      setOrderCanSubmit(false);
      setSubmissionAuthorized(false);
      setOrderStatusError('');
      setOrderStatusChecked(false);
      setOrderVerificationState('idle');
      return;
    }

    let aborted = false;
    let retryTimer = null;
    const params = new URLSearchParams();
    params.set('order_id', orderId);
    if (productId) params.set('product_id', productId);
    if (shaSign && email) {
      params.set('email', email);
      params.set('sha_sign', shaSign);
    }
    const queryString = params.toString();
    const startedAt = Date.now();

    setOrderInfo(null);
    setOrderCanSubmit(false);
    setSubmissionAuthorized(false);
    setOrderStatusError('');
    setOrderStatusChecked(false);
    setOrderVerificationState('verifying');

    const handleFailure = (message) => {
      if (aborted) return;
      setOrderInfo(null);
      setOrderCanSubmit(false);
      setSubmissionAuthorized(false);
      setOrderStatusError(message);
      setOrderStatusChecked(true);
      setOrderVerificationState('failed');
    };

    const scheduleRetry = (delayMs = ORDER_STATUS_DEFAULT_RETRY_MS) => {
      if (aborted) return;
      const elapsed = Date.now() - startedAt;
      if (elapsed >= ORDER_STATUS_POLL_TIMEOUT_MS) {
        handleFailure(ORDER_TIMEOUT_MESSAGE);
        return;
      }
      const safeDelay = Math.min(4000, Math.max(600, delayMs || ORDER_STATUS_DEFAULT_RETRY_MS));
      retryTimer = setTimeout(() => {
        if (!aborted) {
          void pollStatus();
        }
      }, safeDelay);
    };

    const pollStatus = async () => {
      if (aborted) return;
      try {
        const response = await fetch(`${ORDER_STATUS_ENDPOINT}?${queryString}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (aborted) return;

        if (response.status === 202) {
          let retryAfter = ORDER_STATUS_DEFAULT_RETRY_MS;
          try {
            const pending = await response.json();
            const parsed = Number(pending?.retry_after_ms);
            if (Number.isFinite(parsed)) retryAfter = parsed;
          } catch {
            // ignore JSON errors for pending responses
          }
          scheduleRetry(retryAfter);
          return;
        }

        if (response.status === 400) {
          let data = null;
          try {
            data = await response.json();
          } catch {
            data = null;
          }
          const msg = data?.error === 'MISSING_PARAMS'
            ? 'Order ID is required.'
            : 'Could not verify your order. Please check the details or contact support.';
          handleFailure(msg);
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP_${response.status}`);
        }

        const data = await response.json();
        if (data?.portal_access_required) {
          setOrderInfo(null);
          setOrderCanSubmit(false);
          setSubmissionAuthorized(false);
          setOrderStatusError('');
          setOrderStatusChecked(true);
          setOrderVerificationState('authorization_required');
          return;
        }

        if (!data?.order) {
          throw new Error('NO_ORDER');
        }

        const nextCanSubmit = Boolean(data.can_submit);
        const nextSubmissionAuthorized = Boolean(data.submission_authorized);
        const reason = data.reason || '';
        const normalizedProduct = data.order.product_id || '';

        setOrderInfo(data.order);
        setOrderCanSubmit(nextCanSubmit);
        setSubmissionAuthorized(nextSubmissionAuthorized);
        setOrderStatusError('');
        setOrderStatusChecked(true);
        setOrderVerificationState(
          nextCanSubmit
            ? (nextSubmissionAuthorized ? 'verified' : 'authorization_required')
            : reason === 'NO_SLOTS'
              ? 'no_slots'
              : 'blocked'
        );
        setQ((prev) => ({
          ...prev,
          orderId:
            prev.orderId || data.order.order_id || prev.orderId || '',
          productId: prev.productId || normalizedProduct || prev.productId || '',
          plan: data.order.plan_key || data.order.plan || prev.plan || '',
        }));
      } catch (err) {
        if (aborted) return;
        console.error('order status error', err);
        handleFailure('Could not verify your order. Please check the details or contact support.');
      }
    };

    void pollStatus();

    return () => {
      aborted = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [q.orderId, q.productId, q.email, q.shaSign]);

  const cooldownInfo = useMemo(() => {
    if (planNormalized === 'quick_check') return { blocked: false, message: '' };
    const trackValue = resolvedTrackUrl;
    if (!trackValue) return { blocked: false, message: '' };
    const key = spotifyTrackKey(trackValue);
    if (!key) return { blocked: false, message: '' };
    const entry = sharedSpotifyRecords[key];
    if (entry?.pending) {
      return { blocked: true, message: 'Checking track status…' };
    }
    if (entry) {
      const rawUntil = Number(entry.cooldown_until);
      const sharedAt = Number(entry.shared_at);
      let effectiveUntil = null;
      if (Number.isFinite(rawUntil)) effectiveUntil = rawUntil;
      else if (Number.isFinite(sharedAt)) effectiveUntil = sharedAt + THIRTY_DAYS_MS;

      if (Number.isFinite(effectiveUntil)) {
        if (effectiveUntil > Date.now()) {
          const dateStr = new Date(effectiveUntil).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
          return {
            blocked: true,
            message: `${COOLDOWN_BASE_MESSAGE} You can submit it again on ${dateStr}.`,
          };
        }
        return { blocked: false, message: '' };
      }
    }
    if (!entry && sharedSpotifyKeySet.has(key)) {
      return {
        blocked: true,
        message: `${COOLDOWN_BASE_MESSAGE} Please wait 30 days before resubmitting as Standard or Premium.`,
      };
    }
    return { blocked: false, message: '' };
  }, [planNormalized, resolvedTrackUrl, sharedSpotifyRecords, sharedSpotifyKeySet]);

  const cooldownWarning = cooldownInfo.message;
  const inCooldown = cooldownInfo.blocked;

  useEffect(() => {
    const t = titleRef.current;
    const cleanups = [];
    let isMounted = true;

    const startEffects = () => {
      if (!isMounted || !t) {
        return;
      }

      cleanups.push(decodeTextLocked(t, t.dataset.text, 500));
      cleanups.push(applyRandomFlicker(t));
      setTimeout(() => t.classList.add('fade-in'), 0);
    };

    const cancelFontWait = runAfterFontsReady(startEffects);

    document.querySelectorAll('.thank-you-message p').forEach((p, i) => {
      p.classList.add('fade-in-paragraph');
      p.style.animationDelay = `${i * 0.1}s`;
    });
    if (window.innerWidth >= 768) document.body.classList.add('desktop');
    document.querySelectorAll('#social-icons a').forEach((icon, i) => {
      setTimeout(() => {
        icon.style.opacity = '1';
        icon.style.transform = 'translateY(0)';
      }, i * 600);
    });

    return () => {
      isMounted = false;
      cancelFontWait();
      cleanups.forEach((cleanup) => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
    };
  }, []);

  useEffect(() => {
    document.querySelectorAll('.thank-you-message p').forEach((p, i) => {
      p.classList.add('fade-in-paragraph');
      p.style.animationDelay = `${i * 0.1}s`;
    });
  }, [submitted]);

  useEffect(() => {
    const handle = () => {
      const nextQuery = getQuery();
      setQ(nextQuery);
    };
    window.addEventListener('popstate', handle);
    return () => window.removeEventListener('popstate', handle);
  }, []);

  useEffect(() => {
    if (!showVerificationGate) {
      setVerifyDelayReached(false);
      return undefined;
    }
    if (gateTimedOut) {
      setVerifyDelayReached(true);
      return undefined;
    }
    setVerifyDelayReached(false);
    const timer = setTimeout(() => setVerifyDelayReached(true), VERIFY_FAST_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [showVerificationGate, gateTimedOut]);

  useEffect(() => {
    if (orderVerificationState === 'verifying') {
      verifyGateShownAtRef.current = Date.now();
      setVerifyGateHoldActive(false);
      if (verifyGateHoldTimerRef.current) {
        clearTimeout(verifyGateHoldTimerRef.current);
        verifyGateHoldTimerRef.current = null;
      }
      return undefined;
    }
    if (orderVerificationState !== 'verified') {
      setVerifyGateHoldActive(false);
      if (verifyGateHoldTimerRef.current) {
        clearTimeout(verifyGateHoldTimerRef.current);
        verifyGateHoldTimerRef.current = null;
      }
      return undefined;
    }
    const startedAt = verifyGateShownAtRef.current || Date.now();
    const elapsed = Date.now() - startedAt;
    if (elapsed >= MIN_VERIFY_GATE_MS) {
      setVerifyGateHoldActive(false);
      return undefined;
    }
    setVerifyGateHoldActive(true);
    const remaining = MIN_VERIFY_GATE_MS - elapsed;
    const timer = setTimeout(() => {
      setVerifyGateHoldActive(false);
      verifyGateHoldTimerRef.current = null;
    }, remaining);
    verifyGateHoldTimerRef.current = timer;
    return () => {
      clearTimeout(timer);
      verifyGateHoldTimerRef.current = null;
    };
  }, [orderVerificationState]);

  const requestCooldownInfo = useCallback(
    async (trackValue, signal) => {
      const email = emailNormalized;
      if (!email || !email.includes('@')) return null;
      const key = spotifyTrackKey(trackValue);
      if (!key) return null;

      try {
        const params = new URLSearchParams({ email, track: trackValue });
        const resp = await fetch(`${SHARED_TRACKS_ENDPOINT}/check?${params.toString()}`, {
          mode: 'cors',
          credentials: 'include',
          ...(signal ? { signal } : {}),
        });
        if (!resp.ok) return null;
        const data = await resp.json();
        if (!data || data.ok === false || !data.key) return null;

        const found = Boolean(data.found);
        const sharedAtNum =
          typeof data.shared_at === 'number'
            ? data.shared_at
            : (typeof data.shared_at === 'string' ? Number(data.shared_at) : null);
        const cooldownNum =
          typeof data.cooldown_until === 'number'
            ? data.cooldown_until
            : (typeof data.cooldown_until === 'string' ? Number(data.cooldown_until) : null);
        setSharedSpotifyRecords((prev) => {
          const next = { ...prev };
          if (found) {
            next[data.key] = {
              shared_at: Number.isFinite(sharedAtNum) ? sharedAtNum : (prev[data.key]?.shared_at ?? null),
              cooldown_until: Number.isFinite(cooldownNum)
                ? cooldownNum
                : (Number.isFinite(sharedAtNum) ? sharedAtNum + THIRTY_DAYS_MS : (prev[data.key]?.cooldown_until ?? null)),
              pending: false,
            };
          } else if (next[data.key]) {
            delete next[data.key];
          }
          return next;
        });
        if (found) {
          setSharedSpotifyKeys((prev) => (prev.includes(data.key) ? prev : [...prev, data.key]));
        } else {
          setSharedSpotifyKeys((prev) => prev.filter((k) => k !== data.key));
        }

        const canonicalKey = data.canonical_key || (data.key ? `spotify:${data.key}` : null);
        if (data.pending_status && canonicalKey) {
          setPendingRecords((prev) => ({ ...prev, [canonicalKey]: data.pending_status }));
        } else if (canonicalKey) {
          setPendingRecords((prev) => {
            if (!prev[canonicalKey]) return prev;
            const next = { ...prev };
            delete next[canonicalKey];
            return next;
          });
        }

        return {
          key,
          found,
          shared_at: Number.isFinite(sharedAtNum) ? sharedAtNum : null,
          cooldown_until: Number.isFinite(cooldownNum)
            ? cooldownNum
            : (Number.isFinite(sharedAtNum) ? sharedAtNum + THIRTY_DAYS_MS : null),
        };
      } catch (err) {
        if (signal && err?.name === 'AbortError') return null;
        const key = spotifyTrackKey(trackValue);
        if (key) {
          setSharedSpotifyRecords((prev) => {
            const next = { ...prev };
            if (next[key]) next[key] = { ...next[key], pending: false };
            return next;
          });
        }
        return null;
      }
    },
    [emailNormalized],
  );

  useEffect(() => {
    if (!resolvedTrackUrl) return;
    const trackValue = resolvedTrackUrl;
    if (!trackValue) return;
    const key = spotifyTrackKey(trackValue);
    if (!key) return;

    setSharedSpotifyRecords((prev) => {
      const next = { ...prev };
      next[key] = { ...next[key], pending: true };
      return next;
    });

    const controller = new AbortController();
    const timer = setTimeout(() => {
      requestCooldownInfo(trackValue, controller.signal);
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [resolvedTrackUrl, planNormalized, requestCooldownInfo]);

  useEffect(() => {
    if (PORTAL_ONLY_THANK_YOU) return undefined;

    let cancelled = false;
    const email = emailNormalized;
    if (!email || !email.includes('@')) {
      setSharedSpotifyKeys([]);
      setPendingRecords({});
      setPendingCombos({});
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        const url = `${SHARED_TRACKS_ENDPOINT}?email=${encodeURIComponent(email)}`;
        const resp = await fetch(url, { mode: 'cors', credentials: 'include' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        const keys = Array.isArray(data?.spotify_keys) ? data.spotify_keys : [];
        const entries = Array.isArray(data?.records) ? data.records : [];
        const pendingEntries = Array.isArray(data?.pending_records) ? data.pending_records : [];
        const pendingComboEntries = Array.isArray(data?.pending_combos) ? data.pending_combos : [];
        const map = {};
        for (const entry of entries) {
          if (!entry || !entry.key) continue;
          const sharedAt = Number(entry.shared_at);
          const cooldownUntil = Number(entry.cooldown_until);
          const prev = map[entry.key] || {};
          const nextShared = Number.isFinite(sharedAt) ? sharedAt : null;
          const nextCooldown = Number.isFinite(cooldownUntil)
            ? cooldownUntil
            : (Number.isFinite(sharedAt) ? sharedAt + THIRTY_DAYS_MS : null);
          const bestShared = (prev.shared_at || -Infinity) > (nextShared || -Infinity)
            ? prev.shared_at
            : nextShared;
          const bestCooldown = (prev.cooldown_until || -Infinity) > (nextCooldown || -Infinity)
            ? prev.cooldown_until
            : nextCooldown;
          map[entry.key] = {
            shared_at: bestShared != null && bestShared !== -Infinity ? bestShared : null,
            cooldown_until: bestCooldown != null && bestCooldown !== -Infinity ? bestCooldown : null,
            pending: false,
          };
        }
        if (!cancelled) {
          setSharedSpotifyKeys(keys);
          setSharedSpotifyRecords(map);
          const pendingMap = {};
          for (const pending of pendingEntries) {
            if (pending && pending.key) pendingMap[pending.key] = pending.status || 'NEW';
          }
          setPendingRecords(pendingMap);
          const pendingComboMap = {};
          for (const entry of pendingComboEntries) {
            if (entry && entry.combo) pendingComboMap[entry.combo] = entry.status || 'NEW';
          }
          setPendingCombos(pendingComboMap);
        }
      } catch {
        if (!cancelled) {
          setSharedSpotifyKeys([]);
          setSharedSpotifyRecords({});
          setPendingRecords({});
          setPendingCombos({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [emailNormalized]);

  const linkLooksHttp = useMemo(() => (trimmedTrack ? isValidHttpUrl(trimmedTrack) : false), [trimmedTrack]);
  const linkCanBeUsed = useMemo(() => {
    if (!trimmedTrack) return false;
    if (requestType === 'released') {
      return strictMusicProvider === 'spotify' && currentReleasedLinkCheck?.status === 'valid';
    }
    if (strictMusicLinkEntered) return currentReleasedLinkCheck?.status === 'valid';
    return linkLooksHttp;
  }, [requestType, trimmedTrack, strictMusicProvider, strictMusicLinkEntered, currentReleasedLinkCheck, linkLooksHttp]);
  const hasLinkOrFile = useMemo(() => {
    if (requestType === 'released') return linkCanBeUsed;
    if (trimmedTrack && !linkLooksHttp) return false;
    return linkCanBeUsed || Boolean(file);
  }, [requestType, linkCanBeUsed, trimmedTrack, linkLooksHttp, file]);
  const linkErrorMessage = useMemo(() => {
    if (!trimmedTrack) return '';
    if (requestType === 'released') {
      if (strictMusicProvider !== 'spotify') {
        return RELEASED_SPOTIFY_LINK_MESSAGE;
      }
      if (!currentReleasedLinkCheck || currentReleasedLinkCheck.status === 'checking') {
        return RELEASED_SPOTIFY_CHECKING_MESSAGE;
      }
      return currentReleasedLinkCheck.status === 'valid'
        ? ''
        : (currentReleasedLinkCheck.message || RELEASED_SPOTIFY_LINK_MESSAGE);
    }
    if (strictMusicLinkEntered) {
      if (!currentReleasedLinkCheck || currentReleasedLinkCheck.status === 'checking') {
        return SUPPORTED_RELEASE_CHECKING_MESSAGE;
      }
      return currentReleasedLinkCheck.status === 'valid'
        ? ''
        : (currentReleasedLinkCheck.message || SUPPORTED_RELEASE_LINK_MESSAGE);
    }
    return linkLooksHttp ? '' : 'Enter a valid URL (https://...) or clear the field.';
  }, [trimmedTrack, requestType, strictMusicProvider, strictMusicLinkEntered, currentReleasedLinkCheck, linkLooksHttp]);
  const notesErrorMessage = useMemo(
    () => (containsForbiddenNotesLink(notes) ? NOTES_LINK_NOT_ALLOWED_MESSAGE : ''),
    [notes]
  );
  const notesLimitMessage = notes.length >= SUBMISSION_NOTES_MAX ? SUBMISSION_NOTES_LIMIT_MESSAGE : '';
  const handleFileChange = useCallback((event) => {
    const nextFile = event.target.files?.[0] || null;
    if (!nextFile) {
      setFile(null);
      setFileErrorMessage('');
      return;
    }
    if (!isSupportedSubmissionAudioFile(nextFile)) {
      event.target.value = '';
      setFile(null);
      setFileErrorMessage(UNSUPPORTED_SUBMISSION_FILE_MESSAGE);
      return;
    }
    setFile(nextFile);
    setFileErrorMessage('');
    if (errorMsg === UNSUPPORTED_SUBMISSION_FILE_MESSAGE) {
      setErrorMsg('');
    }
  }, [errorMsg]);

  const collectSubmitBlockReasons = useCallback(() => {
    const errs = [];
    if (!orderAllowsSubmission) {
      if (orderVerificationState === 'verifying') {
        errs.push('We are still confirming your payment. Please wait a moment.');
      } else if (orderVerificationState === 'no_slots') {
        errs.push('All prepaid reviews for this order are already used.');
      } else if (orderVerificationState === 'blocked') {
        errs.push('This order cannot be used for submissions. Contact support if you need help.');
      } else {
        errs.push(orderStatusError || 'Enter a valid order with available slots.');
      }
    }
    if (quickCheckWarning && requestType !== 'review_only') errs.push(quickCheckWarning);
    if (inCooldown) {
      errs.push(cooldownWarning || `${COOLDOWN_BASE_MESSAGE} Please wait 30 days before resubmitting as Standard or Premium.`);
    }
    if (!agree) errs.push('Confirm the rights checkbox.');
    if (!artist.trim()) errs.push('Artist / Project name is required.');
    if (!songName.trim()) errs.push('Song name is required.');
    if (fileErrorMessage) errs.push(fileErrorMessage);
    if (notesErrorMessage) errs.push(notesErrorMessage);
    if (requestType === 'released') {
      if (!linkCanBeUsed) {
        errs.push(linkErrorMessage || 'Spotify link is required for released tracks.');
      }
    } else if (requestType === 'planned') {
      if (trimmedTrack && linkErrorMessage) errs.push(linkErrorMessage);
      if (!hasLinkOrFile) errs.push('Provide a link OR attach an audio file.');
      if (!plannedDate) errs.push('Select the planned release date.');
    } else {
      if (trimmedTrack && linkErrorMessage) errs.push(linkErrorMessage);
      if (!hasLinkOrFile) errs.push('Provide a link OR attach an audio file.');
    }
    return errs;
  }, [orderAllowsSubmission, orderVerificationState, orderStatusError, quickCheckWarning, inCooldown, cooldownWarning, agree, artist, songName, fileErrorMessage, notesErrorMessage, requestType, trimmedTrack, linkCanBeUsed, linkErrorMessage, hasLinkOrFile, plannedDate]);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!orderAllowsSubmission) return false;
    if (!agree || !artist.trim() || !songName.trim()) return false;
    if (notesErrorMessage) return false;
    if ((quickCheckWarning && requestType !== 'review_only') || inCooldown) return false;
    if (hasPendingDuplicate) return false;

    if (requestType === 'released') {
      return linkCanBeUsed;
    }
    if (requestType === 'planned') {
      if (trimmedTrack && linkErrorMessage) return false;
      return hasLinkOrFile && !!plannedDate;
    }
    if (trimmedTrack && linkErrorMessage) return false;
    return hasLinkOrFile;
  }, [agree, artist, songName, notesErrorMessage, submitting, requestType, linkCanBeUsed, plannedDate, hasLinkOrFile, quickCheckWarning, inCooldown, hasPendingDuplicate, orderAllowsSubmission, linkErrorMessage, trimmedTrack]);

  const submitBlockReason = useMemo(() => {
    const reasons = collectSubmitBlockReasons();
    return reasons.length ? reasons[0] : '';
  }, [collectSubmitBlockReasons]);

  // ===== Autocomplete from link (Spotify / SoundCloud) — YouTube disabled =====
  const completeAutofillLock = useCallback((url, { success, message }) => {
    if (pendingAutofillUrlRef.current && url && pendingAutofillUrlRef.current !== url) {
      return;
    }
    if (autofillLockTimerRef.current) {
      clearTimeout(autofillLockTimerRef.current);
      autofillLockTimerRef.current = null;
    }
    setAutofillLocked(false);
    if (success) {
      setAutofillStatus('done');
      setAutofillMessage('');
    } else if (message) {
      setAutofillStatus('failed');
      setAutofillMessage(message);
    }
  }, []);

  const beginAutofillLock = useCallback((url) => {
    pendingAutofillUrlRef.current = url;
    setAutofillLocked(true);
    setAutofillStatus('checking');
    setAutofillMessage('Checking your link — artist & song will auto-fill if possible.');
    if (autofillLockTimerRef.current) {
      clearTimeout(autofillLockTimerRef.current);
    }
    autofillLockTimerRef.current = setTimeout(() => {
      setAutofillLocked(false);
      setAutofillStatus('timeout');
      setAutofillMessage("Couldn't fetch automatically — please type manually.");
      autofillLockTimerRef.current = null;
    }, 5000);
  }, []);

  const autofillFromLink = useCallback(async (url) => {
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, '');

      let nextArtist = '';
      let nextSong   = '';

      // ---------- SPOTIFY (spotify-url-info + прокси) ----------
      if (isSpotifyUrl(url)) {
        // Можно заменить на свой Cloudflare Worker:
        // const PROXY_BASE = 'https://<your-worker>.workers.dev/?u=';
        const PROXY_BASE = PRIMARY_PROXY_BASE;

        // Две попытки + cache-busting для нестабильного прокси
        nextArtist = ''; nextSong = '';
        for (let i = 0; i < 2; i++) {
          try {
            const urlNC = addNoCacheParam(url);
            const { artist: a, title: t } = await getSpotifyArtistAndTitle(urlNC, { proxyBase: PROXY_BASE });
            if (a) nextArtist = a;
            if (t) nextSong   = t;
            if (nextArtist || nextSong) break;
          } catch (e) {
            // пробуем второй раз
          }
        }

        // --- ФОЛБЭК ДЛЯ АЛЬБОМА (НЕ СИНГЛ) ---
        // Если это /album/ и артист пустой — пробуем oEmbed, затем (если надо) HTML (OG).
        const isAlbumUrl = /\/album\//.test(url);
        if (isAlbumUrl && !nextArtist) {
          // 1) oEmbed через прокси (с ретраями + cache-busting)
          try {
            const oembedUrl = `https://open.spotify.com/oembed?url=${encodeURIComponent(addNoCacheParam(url))}`;
            const meta = await proxiedFetchJSON(PROXY_BASE, oembedUrl, 2);
            const candidate = (meta.author_name || '').trim();
            const provider  = (meta.provider_name || '').trim();
            if (candidate && (!provider || candidate.toLowerCase() !== provider.toLowerCase())) {
              nextArtist = candidate;
              // По ТЗ: для обычного альбома — название трека не подставляем (nextSong пустой)
            }
          } catch (e) {
            // тихо идём к следующему фолбэку
          }

          // 2) OG-теги HTML-страницы альбома
          if (!nextArtist) {
            try {
              const html = await proxiedFetchText(PROXY_BASE, url, 2);
              const m = html.match(/<meta\s+property=["']og:description["']\s+content=["']([^"']+)["']/i);
              if (m && m[1]) {
                // Ищем: "... an album by ARTIST on Spotify"
                const d = m[1];
                const mm = d.match(/an\s+album\s+by\s+(.+?)\s+on\s+spotify/i);
                if (mm && mm[1]) {
                  nextArtist = mm[1].trim();
                }
              }
              // og:title здесь не используем — для обычного альбома трек не нужен
            } catch (e) {
              // оставляем как есть
            }
          }
        }

      // ---------- SOUNDCLOUD (оставляем твою рабочую реализацию) ----------
      } else if (host.includes('soundcloud.com') || host.includes('on.soundcloud.com')) {
        const oembedUrl = `https://soundcloud.com/oembed?format=json&url=${encodeURIComponent(url)}`;
        let meta = null;
        try {
          const resp = await fetch(oembedUrl, { mode: 'cors' });
          if (resp.ok) meta = await resp.json();
        } catch (e) {}

        if (meta) {
          const title = meta.title || '';
          const rawAuthor = (meta.author_name || '').trim();
          const provider  = (meta.provider_name || '').trim();
          const author = (/^soundcloud\b/i.test(rawAuthor) || (provider && rawAuthor.toLowerCase() === provider.toLowerCase())) ? '' : rawAuthor;

          const { artist: aFromTitle, song: sFromTitle } = splitArtistSong(title);

          if (author && aFromTitle && aFromTitle.toLowerCase() !== author.toLowerCase()) {
            nextArtist = author;
            nextSong   = sFromTitle || title;
          } else {
            nextArtist = aFromTitle || author || '';
            nextSong   = sFromTitle || title  || '';
          }
          if (!nextSong && title) nextSong = stripTitleNoise(title);

          // Поддержка шаблона "Song by Artist"
          const m = title.match(/^\s*(.+?)\s+by\s+(.+)\s*$/i);
          if (m) {
            const scSong   = cleanField(m[1]);
            const scArtist = cleanField(m[2]);
            if (!nextArtist) nextArtist = scArtist;
            nextSong = scSong;
          }
        } else {
          // Фолбэк: /artist/track-slug
          const seg = u.pathname.split('/').filter(Boolean);
          if (seg.length >= 2) {
            nextArtist = decodeURIComponent(seg[0]).replace(/[-_]+/g, ' ');
            nextSong   = decodeURIComponent(seg[1]).replace(/[-_]+/g, ' ');
          }
        }

      } else if (isAppleMusicAutofillUrl(url) || isYouTubeMusicAutofillUrl(url)) {
        const meta = await fetchRichLinkMeta(url);
        if (meta && meta.autofill_ok) {
          nextArtist = meta.artist || '';
          nextSong = meta.title || '';
        }
      } else {
        // Другие провайдеры не трогаем
        completeAutofillLock(url, { success: true });
        return;
      }

      // ---- финальная зачистка ----
      const stripProviderSuffixLocal = (s = '') =>
        String(s).replace(/\s*[-–—|·•]\s*(?:on\s+)?spotify\s*$/i, '').trim();
      if (nextArtist) nextArtist = stripProviderSuffixLocal(nextArtist);
      if (nextSong)   nextSong   = stripProviderSuffixLocal(nextSong).replace(/\s+by\s+.+$/i, '').trim();

      // Не перезаписываем вручную изменённые поля (как и раньше)
      const canOverrideArtist = (() => {
        const isNewUrl = lastAutofillRef.current.url !== url;
        return isNewUrl ? true : (!artistDirty || artist === lastAutofillRef.current.artist || !artist);
      })();
      const canOverrideSong = !songDirty || songName === lastAutofillRef.current.song || !songName;

      if (canOverrideArtist && nextArtist) { setArtist(nextArtist); setArtistDirty(false); }
      if (canOverrideSong   && nextSong)   { setSongName(nextSong); setSongDirty(false); }

      // Запоминаем, что подставили
      lastAutofillRef.current = {
        url,
        artist: nextArtist || artist,
        song: nextSong || songName
      };

      // Если ничего не подставили — разрешим повторные попытки для той же ссылки
      if (!nextArtist && !nextSong) {
        lastLookupRef.current.url = '';
        completeAutofillLock(url, { success: false, message: "Couldn't fetch automatically — please type manually." });
      } else {
        completeAutofillLock(url, { success: true });
      }

    } catch (e) {
      completeAutofillLock(url, { success: false, message: "Couldn't fetch automatically — please type manually." });
    }
  }, [artistDirty, artist, songDirty, songName, completeAutofillLock]);


  useEffect(() => {
    const v = (track || '').trim();
    if (!v || !/^https?:\/\//i.test(v)) {
      pendingAutofillUrlRef.current = '';
      if (autofillLockTimerRef.current) {
        clearTimeout(autofillLockTimerRef.current);
        autofillLockTimerRef.current = null;
      }
      setAutofillLocked(false);
      setAutofillStatus('idle');
      setAutofillMessage('');
      return;
    }
    if (lastLookupRef.current.url === v) return;
    if (autofillTimerRef.current) clearTimeout(autofillTimerRef.current);
    beginAutofillLock(v);
    autofillTimerRef.current = setTimeout(() => {
      lastLookupRef.current.url = v;
      autofillFromLink(v);
    }, 500);
    return () => { if (autofillTimerRef.current) clearTimeout(autofillTimerRef.current); };
  }, [track, autofillFromLink, beginAutofillLock]); // ссылка или логика автозаполнения

  useEffect(() => {
    autoGrow(notesRef.current);
  }, [notes]);

  useEffect(() => () => {
    if (autofillLockTimerRef.current) {
      clearTimeout(autofillLockTimerRef.current);
      autofillLockTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isQuickCheckPlan || requestType === 'review_only') {
      setQuickCheckWarning('');
      return;
    }
    const key = spotifyTrackKey(resolvedTrackUrl);
    if (key && sharedSpotifyKeySet.has(key)) {
      setQuickCheckWarning(QUICK_CHECK_DUPLICATE_MESSAGE);
    } else {
      setQuickCheckWarning('');
    }
  }, [isQuickCheckPlan, requestType, sharedSpotifyKeySet, resolvedTrackUrl]);

  useEffect(() => {
    if (!quickCheckWarning && errorMsg === QUICK_CHECK_DUPLICATE_MESSAGE) {
      setErrorMsg('');
    }
    if (!cooldownWarning && errorMsg && errorMsg.startsWith(COOLDOWN_BASE_MESSAGE)) {
      setErrorMsg('');
    }
    if (!notesErrorMessage && errorMsg === NOTES_LINK_NOT_ALLOWED_MESSAGE) {
      setErrorMsg('');
    }
    if (!fileErrorMessage && errorMsg === UNSUPPORTED_SUBMISSION_FILE_MESSAGE) {
      setErrorMsg('');
    }
  }, [errorMsg, quickCheckWarning, cooldownWarning, notesErrorMessage, fileErrorMessage]);

  function showWhyCantSubmit() {
    const errs = collectSubmitBlockReasons();
    if (errs.length) setErrorMsg(errs.join(' '));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (PORTAL_ONLY_THANK_YOU) return;

    setErrorMsg('');
    if (file && !isSupportedSubmissionAudioFile(file)) {
      setFile(null);
      setFileErrorMessage(UNSUPPORTED_SUBMISSION_FILE_MESSAGE);
      setErrorMsg(UNSUPPORTED_SUBMISSION_FILE_MESSAGE);
      return;
    }
    if (notesErrorMessage) {
      setErrorMsg(notesErrorMessage);
      return;
    }
    if (!orderAllowsSubmission) {
      if (orderVerificationState === 'verifying') {
        setErrorMsg('We are still confirming your payment. Please wait a moment.');
      } else if (orderVerificationState === 'no_slots') {
        setErrorMsg('All prepaid reviews for this order are already used.');
      } else if (orderVerificationState === 'blocked') {
        setErrorMsg('This order cannot be used for submissions. Contact support if you need help.');
      } else {
        setErrorMsg(orderStatusError || 'We could not verify any available slots for this order.');
      }
      return;
    }
    let submitTrackValue = trimmedTrack;
    if (trimmedTrack && strictMusicLinkEntered) {
      if (requestType === 'released' && strictMusicProvider !== 'spotify') {
        setErrorMsg(RELEASED_SPOTIFY_LINK_MESSAGE);
        return;
      }
      let releaseCheck = currentReleasedLinkCheck;
      if (!releaseCheck || releaseCheck.status !== 'valid') {
        setReleasedLinkCheck(makeReleasedLinkChecking(trimmedTrack));
        try {
          releaseCheck = await runReleasedLinkValidation(trimmedTrack);
          setReleasedLinkCheck(releaseCheck);
        } catch {
          releaseCheck = makeReleasedLinkUnverified(trimmedTrack);
          setReleasedLinkCheck(releaseCheck);
        }
      }
      if (!releaseCheck || releaseCheck.status !== 'valid') {
        setErrorMsg(
          releaseCheck?.message ||
            (requestType === 'released'
              ? RELEASED_SPOTIFY_LINK_MESSAGE
              : SUPPORTED_RELEASE_LINK_MESSAGE)
        );
        return;
      }
      submitTrackValue = releaseCheck.canonicalUrl || releaseCheck.normalizedUrl || trimmedTrack;
    } else if (requestType === 'released') {
      setErrorMsg(RELEASED_SPOTIFY_LINK_MESSAGE);
      return;
    }
    if (planNormalized === 'quick_check' && requestType !== 'review_only' && submitTrackValue) {
      const key = spotifyTrackKey(submitTrackValue);
      if (key && sharedSpotifyKeySet.has(key)) {
        setQuickCheckWarning(QUICK_CHECK_DUPLICATE_MESSAGE);
        setErrorMsg(QUICK_CHECK_DUPLICATE_MESSAGE);
        return;
      }
    }
    if (planNormalized !== 'quick_check' && submitTrackValue) {
      const info = await requestCooldownInfo(submitTrackValue);
      const cooldownUntil = info?.cooldown_until;
      if (Number.isFinite(cooldownUntil) && cooldownUntil > Date.now()) {
        const dateStr = new Date(cooldownUntil).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        setErrorMsg(`${COOLDOWN_BASE_MESSAGE} You can submit it again on ${dateStr}.`);
        return;
      }
    }
    const pendingStatus = submitTrackValue ? pendingRecords[canonicalLinkKey(submitTrackValue)] : '';
    if (pendingStatus) {
      const pendingMessage = pendingStatus === 'APPROVED'
        ? 'This track link is already approved and waiting to be shared.'
        : 'This track link was already submitted and is awaiting review.';
      setErrorMsg(pendingMessage);
      return;
    }
    const baseReady =
      orderAllowsSubmission &&
      !submitting &&
      !!agree &&
      !!artist.trim() &&
      !!songName.trim() &&
      !(quickCheckWarning && requestType !== 'review_only') &&
      !inCooldown &&
      !hasPendingDuplicate;
    const linkFieldValid = !trimmedTrack || (strictMusicLinkEntered ? !!submitTrackValue : linkLooksHttp);
    const linkOrFileReady = requestType === 'released'
      ? !!submitTrackValue
      : trimmedTrack
      ? (linkFieldValid && (Boolean(submitTrackValue) || Boolean(file)))
      : Boolean(file);
    const localCanSubmit = requestType === 'released'
      ? (baseReady && !!submitTrackValue)
      : requestType === 'planned'
      ? (baseReady && !!plannedDate && linkOrFileReady)
      : (baseReady && linkOrFileReady);
    if (!localCanSubmit) {
      showWhyCantSubmit();
      return;
    }
    setSubmitting(true);
    try {
      let resp;
      if (file) {
        const formData = new FormData();
        formData.append('order_id', q.orderId);
        formData.append('product_id', q.productId);
        formData.append('plan', orderPlanKey);
        formData.append('from', 'thankyou');
        formData.append('artist', artist);
        formData.append('song_name', songName);
        formData.append('track', submitTrackValue);
        formData.append('notes', notes);
        if (reviewStyleSubmitValue) {
          formData.append('review_style', reviewStyleSubmitValue);
        }
        formData.append('file', file);
        formData.append('request_type', requestType);
        if (requestType === 'planned') {
          formData.append('planned_release_date', plannedDate);
        }
        resp = await fetch(SUBMIT_ENDPOINT, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
      } else {
        const payload = {
          order_id: q.orderId,
          product_id: q.productId,
          plan: orderPlanKey,
          artist,
          song_name: songName,
          track: submitTrackValue,
          notes,
          review_style: reviewStyleSubmitValue,
          request_type: requestType,
          from: 'thankyou',
        };
        if (!reviewStyleSubmitValue) delete payload.review_style;
        if (requestType === 'planned') payload.planned_release_date = plannedDate;
        resp = await fetch(SUBMIT_ENDPOINT, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      let data = null;
      try {
        data = await resp.clone().json();
      } catch {
        data = null;
      }
      if (resp.status === 401) {
        setSubmissionAuthorized(false);
        setOrderVerificationState('authorization_required');
        setErrorMsg('');
        return;
      }
      if (!resp.ok || (data && data.ok === false)) {
        const msg =
          (data && (data.message || data.error)) ||
          (data?.code === 'TRACK_ALREADY_SHARED' ? QUICK_CHECK_DUPLICATE_MESSAGE : null) ||
          `Server responded ${resp.status}`;
        const error = new Error(msg);
        if (data?.code) error.code = data.code;
        if (data?.available_at) error.available_at = data.available_at;
        if (data?.shared_at) error.shared_at = data.shared_at;
        throw error;
      }
      const canonicalKey = canonicalLinkKey(submitTrackValue);
      if (canonicalKey) {
        setPendingRecords((prev) => ({ ...prev, [canonicalKey]: 'NEW' }));
      }
      const comboKey = normalizeComboKey(artist, songName);
      if (comboKey) {
        setPendingCombos((prev) => ({ ...prev, [comboKey]: 'NEW' }));
      }
      trackSiteEvent('submission_form_submitted', {
        source_page: 'thank_you',
        order_id: q.orderId || '',
        plan_key: orderPlanKey || '',
        request_type: requestType,
        used_file: Boolean(file),
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      trackSiteEvent('submission_form_error', {
        source_page: 'thank_you',
        order_id: q.orderId || '',
        plan_key: orderPlanKey || '',
        request_type: requestType,
        error_code: err?.code || err?.message || 'SUBMISSION_FAILED',
      });
      let customMsg = '';
      if (err?.code === 'TRACK_ALREADY_SHARED' && requestType !== 'review_only') {
        const key = spotifyTrackKey(submitTrackValue);
        if (key) {
          setSharedSpotifyKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
        }
        setQuickCheckWarning(QUICK_CHECK_DUPLICATE_MESSAGE);
        customMsg = QUICK_CHECK_DUPLICATE_MESSAGE;
      } else if (err?.code === 'TRACK_IN_COOLDOWN') {
        const key = spotifyTrackKey(submitTrackValue);
        if (key) {
          setSharedSpotifyKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
          setSharedSpotifyRecords((prev) => {
            const next = { ...prev };
            const prevEntry = next[key] || {};
            const sharedAt = Number(err.shared_at);
            const availableAt = Number(err.available_at);
            next[key] = {
              shared_at: Number.isFinite(sharedAt) ? sharedAt : (prevEntry.shared_at ?? null),
              cooldown_until: Number.isFinite(availableAt)
                ? availableAt
                : (prevEntry.cooldown_until ?? null),
            };
            return next;
          });
        }
        const availableAt = Number(err?.available_at);
        if (Number.isFinite(availableAt)) {
          const dateStr = new Date(availableAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
          customMsg = `${COOLDOWN_BASE_MESSAGE} You can submit it again on ${dateStr}.`;
        }
      } else if (err?.code === 'TRACK_PENDING') {
        const canonicalKey = canonicalLinkKey(submitTrackValue);
        if (canonicalKey) {
          setPendingRecords((prev) => ({ ...prev, [canonicalKey]: 'NEW' }));
        }
        const comboKey = normalizeComboKey(artist, songName);
        if (comboKey) {
          setPendingCombos((prev) => ({ ...prev, [comboKey]: 'NEW' }));
        }
        customMsg = err?.message || 'This track was already submitted and is awaiting review.';
      }
      setErrorMsg(customMsg || err?.message || `Upload failed. Please try again or email ${SUPPORT_EMAIL}.`);
    } finally {
      setSubmitting(false);
    }
  }

  const submitProgressMessage = file ? 'Uploading your track…' : 'Submitting your request…';
  const submitProgressNote = file
    ? 'Please keep this page open until the upload completes.'
    : 'Please wait — do not refresh this page.';

  const renderVerificationGate = () => (
    <div className={`verify-gate${verifyGateHoldActive ? ' verify-gate--finishing' : ''}`}>
      <OrderVerificationLoader
        state="verifying"
        showCaption={false}
        colorMode={verifyDelayReached ? 'full' : 'reveal'}
        revealDurationMs={verifyGateHoldActive ? Math.min(MIN_VERIFY_GATE_MS, VERIFY_FAST_WINDOW_MS) : VERIFY_FAST_WINDOW_MS}
      />
      {!verifyDelayReached ? (
        <TypewriterText
          className="verify-gate__copy"
          segments={verifyTextSegments}
          animate={showVerificationGate}
          animationKey="verify"
        />
      ) : (
        <TypewriterText
          className="verify-gate__copy"
          segments={verifyDelaySegments}
          animate={verifyDelayReached}
          animationKey="verify_delay"
        />
      )}
    </div>
  );

  const renderPortalPurpose = () => (
    <div
      style={{
        margin: '24px 0',
        padding: '16px 18px',
        borderLeft: '2px solid rgba(var(--thankyou-rgb), 0.78)',
        background: 'rgba(var(--thankyou-rgb), 0.055)',
      }}
    >
      <div
        style={{
          marginBottom: '7px',
          color: 'var(--thankyou-tone)',
          fontFamily: "'Tektur', system-ui, sans-serif",
          fontSize: '0.7rem',
          letterSpacing: '0.12em',
        }}
      >
        YOUR CLIENT PORTAL
      </div>
      <p style={{ margin: 0, color: 'rgba(255,255,255,0.72)', fontSize: '0.9rem' }}>
        Submit tracks, see your available reviews, track your submissions, and receive feedback — all in one place.
      </p>
    </div>
  );

  const portalGateStyle = {
    width: '100%',
    maxWidth: 'calc(100vw - 32px)',
    marginTop: 0,
    padding: 'clamp(24px, 5vw, 38px)',
    boxSizing: 'border-box',
    color: 'rgba(255,255,255,0.84)',
    overflowWrap: 'anywhere',
    textAlign: 'left',
  };

  const renderPortalEyebrow = () => (
    <div
      style={{
        marginBottom: '10px',
        color: '#57ff9d',
        fontFamily: "'Tektur', system-ui, sans-serif",
        fontSize: '0.74rem',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
      }}
    >
      PAYMENT CONFIRMED
    </div>
  );

  const portalHeadingStyle = {
    margin: '0 0 18px',
    color: '#fff',
    fontFamily: "'Tektur', system-ui, sans-serif",
    fontSize: 'clamp(1.45rem, 5vw, 2.15rem)',
    fontWeight: 500,
    letterSpacing: '0.08em',
  };

  const portalHelpStyle = {
    paddingTop: '16px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.63)',
    fontSize: '0.86rem',
    lineHeight: 1.55,
  };

  const renderEmailAuthorizationGate = () => (
    <section className="portal-callout thankyou-glass-panel" style={portalGateStyle}>
      {renderPortalEyebrow()}
      <h1 style={portalHeadingStyle}>CHECK YOUR EMAIL</h1>
      <p style={{ lineHeight: 1.6 }}>
        We sent your client portal link to the email address used for this purchase.
      </p>
      <p style={{ lineHeight: 1.6 }}>
        Open the payment email we sent you and click <strong>Open your client portal</strong>.
      </p>

      {renderPortalPurpose()}

      <div style={portalHelpStyle}>
        Can&apos;t find it? Check <strong>Spam</strong> and <strong>Promotions</strong>, or{' '}
        <a
          href={requestLoginLink}
          style={{
            display: 'inline',
            margin: 0,
            padding: 0,
            background: 'none',
            color: 'var(--thankyou-tone)',
            font: 'inherit',
            textAlign: 'inherit',
            textDecoration: 'underline',
          }}
        >
          request a new link
        </a>.
      </div>
    </section>
  );

  const renderLegacyPortalGate = () => (
    <section className="portal-callout thankyou-glass-panel" style={portalGateStyle}>
      {renderPortalEyebrow()}
      <h1
        style={{
          ...portalHeadingStyle,
          fontSize: 'clamp(1.2rem, 4.4vw, 1.85rem)',
          letterSpacing: '0.06em',
        }}
      >
        YOUR CLIENT PORTAL
      </h1>
      <p style={{ lineHeight: 1.6 }}>
        Open your portal to see your unused prepaid reviews. If needed, sign in with the email you used for your purchase.
      </p>

      <div style={{ margin: '25px 0 24px' }}>
        <a
          className="portal-purchase-cta"
          href={genericClientPortalPath}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '44px',
            maxWidth: '100%',
            margin: 0,
            padding: '0 18px',
            boxSizing: 'border-box',
            border: '1px solid var(--thankyou-tone)',
            background: 'var(--thankyou-tone)',
            color: '#001416',
            fontFamily: "'Tektur', system-ui, sans-serif",
            fontSize: '0.76rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textDecoration: 'none',
          }}
        >
          OPEN YOUR CLIENT PORTAL
        </a>
      </div>

      <div style={portalHelpStyle}>
        Your available prepaid reviews are shown in your portal. Need help? Contact{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </div>
    </section>
  );

  const renderPortalReadyGate = () => {
    const reviewNoun = tracksRemaining === 1 ? 'review' : 'reviews';
    const availabilityVerb = tracksRemaining === 1 ? 'is' : 'are';

    return (
      <section className="portal-callout thankyou-glass-panel" style={portalGateStyle}>
        {renderPortalEyebrow()}
        <h1 style={portalHeadingStyle}>YOUR REVIEW IS READY</h1>
        <p style={{ lineHeight: 1.6 }}>
          <strong>{tracksRemaining} {portalPlanLabel} {reviewNoun}</strong>{' '}
          {availabilityVerb} available in your account.
        </p>
        <p style={{ lineHeight: 1.6 }}>
          You&apos;re already signed in. Open your client portal to submit your track.
        </p>

        <div style={{ margin: '25px 0 24px' }}>
          <a
            className="portal-purchase-cta"
            href={clientSubmitPath}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '44px',
              maxWidth: '100%',
              margin: 0,
              padding: '0 18px',
              boxSizing: 'border-box',
              border: '1px solid var(--thankyou-tone)',
              background: 'var(--thankyou-tone)',
              color: '#001416',
              fontFamily: "'Tektur', system-ui, sans-serif",
              fontSize: '0.76rem',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textDecoration: 'none',
            }}
          >
            OPEN YOUR CLIENT PORTAL
          </a>
          <div
            style={{
              marginTop: '10px',
              color: 'rgba(255,255,255,0.55)',
              fontSize: '0.78rem',
              lineHeight: 1.45,
            }}
          >
            The submission form will open with your new {portalPlanLabel} review selected.
          </div>
        </div>

        {renderPortalPurpose()}

        <div style={portalHelpStyle}>
          We also sent your payment confirmation to the email address used for this purchase.
        </div>
      </section>
    );
  };

  const renderPreSubmitMessage = () => {
    if (showVerificationGate) {
      return renderVerificationGate();
    }

    if (showEmailAuthorizationGate) {
      return renderEmailAuthorizationGate();
    }

    if (q.hasLegacyOrderLink) {
      return renderLegacyPortalGate();
    }

    if (orderVerificationState === 'idle' && !q.orderId) {
      return (
        <>
          <p className={`warn ${ATTN_RED}`}>We couldn&apos;t find an order in this link.</p>
          <p>
            Open the button from your payment email, or contact{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </>
      );
    }

    if (!orderAllowsSubmission) {
      if (orderVerificationState === 'no_slots') {
        return (
          <>
            <p className={`warn ${ATTN_BLUE}`}>
              All prepaid reviews for this order are already used. We can&apos;t unlock another submission slot.
            </p>
            <p>
              Need help? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we&apos;ll look into it.
            </p>
          </>
        );
      }
      if (orderVerificationState === 'blocked') {
        return (
          <>
            <p className={`warn ${ATTN_BLUE}`}>
              This order is confirmed but not eligible for submissions. If this seems wrong, reach out to us.
            </p>
            <p>
              Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and include your order ID.
            </p>
          </>
        );
      }
      if (orderVerificationState === 'failed') {
        return (
          <>
            <p className={`warn ${ATTN_RED}`}>
              {orderStatusError || 'We could not verify this order yet. Double-check the details below.'}
            </p>
            <p>
              Message <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> for manual help.
            </p>
          </>
        );
      }
    }

    if (
      orderVerificationState === 'verified' &&
      orderAllowsSubmission &&
      hasCanonicalInternalOrderId
    ) {
      return renderPortalReadyGate();
    }

    if (
      orderVerificationState === 'verified' &&
      orderAllowsSubmission &&
      !hasCanonicalInternalOrderId
    ) {
      return renderLegacyPortalGate();
    }

    return (
      <>
        <p className={`warn ${ATTN_RED}`}>We could not open this order safely.</p>
        <p>
          Open the latest button from your payment email, or contact{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </>
    );
  };

  const trackPlaceholder =
    requestType === 'released'
      ? 'Spotify track link, or album link only if it is a 1-track single'
      : 'Spotify / Apple Music / SoundCloud / YouTube / direct link (or upload a file below)';

  return (
    <div className={`App thankyou-page ${thankYouPageThemeClass}`} style={{ maxWidth: '100vw' }}>
      <style>{`
        @media (max-width: 600px) {
          .thankyou-page .portal-purchase-cta {
            width: 100% !important;
          }
        }
      `}</style>
          {/* Order banner (shows after payment) */}
          {/*{q.orderId && (
        <div style={{
          position:'sticky', top:0, zIndex:1000,
          background:'#111', color:'#fff',
          border:'1px solid #333', borderRadius:'0px',
          margin:'12px auto', padding:'10px 14px', maxWidth:720,
          display:'flex', alignItems:'center', gap:'10px'
        }}>
          <strong>Order ID:</strong>
          <code style={{background:'#222', padding:'4px 8px', borderRadius:6}}>
            {q.orderId}
          </code>
          <button
            type="button"
            onClick={() => { navigator.clipboard?.writeText(q.orderId); }}
            style={{marginLeft:'auto', padding:'6px 10px', borderRadius:0, border:0, background:'#e11', color:'#fff', cursor:'pointer'}}
            title="Copy Order ID"
          >
            Copy
          </button>
        </div>
      )}*/}

      <div id="background">
        <img src={require('./bg_noise/mobile_noise.gif')} alt="" aria-hidden="true" className="bg-gif mobile" />
        <img src={require('./bg_noise/tablet_noise.gif')} alt="" aria-hidden="true" className="bg-gif tablet" />
        <img src={require('./bg_noise/desktop_noise.gif')} alt="" aria-hidden="true" className="bg-gif desktop" />
      </div>

      <div id="content-wrapper">
        <div
          id="links"
          className="thankyou-links"
          style={PORTAL_ONLY_THANK_YOU ? { margin: 0 } : undefined}
        >
          <div className="thank-you-title" data-text="THANK YOU!" ref={titleRef}>THANK YOU!</div>

          {/* Order status summary removed */}

          <div
            className="thank-you-message"
            style={{ width: 'min(100%, 620px)', padding: 0 }}
          >
            {renderPreSubmitMessage()}
          </div>

          {!PORTAL_ONLY_THANK_YOU && !showVerificationGate && !showEmailAuthorizationGate && (
            <div className="order-status-panel thankyou-glass-panel">
              {orderInfo && tracksTotal > 0 && (
                <p>
                  <span className="plan-label">{orderPlanKey || planNormalized || 'selected plan'}</span> •{' '}
                  <strong>{tracksRemaining}</strong>/<strong>{tracksTotal}</strong> slots available
                </p>
              )}
              {orderVerificationState === 'blocked' && (
                <p className={`warn ${ATTN_BLUE}`}>
                  This order is confirmed but not eligible for submissions. Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> if this is unexpected.
                </p>
              )}
              {orderVerificationState === 'failed'
                && orderStatusChecked
                && !orderInfo
                && (q.orderId || q.email || q.productId) && (
                  <p className={`warn ${ATTN_RED}`}>
                    {orderStatusError || 'We could not verify this order yet. Double-check the details below.'}
                  </p>
              )}
            </div>
          )}

          {!PORTAL_ONLY_THANK_YOU && !submitted && !showVerificationGate && !showEmailAuthorizationGate && orderAllowsSubmission && (
            <form className={`track-form ${thankYouFormThemeClass}`} onSubmit={handleSubmit}>
              {orderAllowsSubmission && (
                <>
                  {/* ───────── Type chooser (ALWAYS VISIBLE) ───────── */}
                  <div className="form-row">
                    <label className="type-title">
                      Choose the correct option. <span className="req">(required)</span>
                    </label>
                    <div className="type-panel" role="group" aria-label="Submission type">
                  {/* Released */}
                  <label className={`type-item ${requestType === 'released' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="request_type"
                      value="released"
                      checked={requestType === 'released'}
                      onChange={() => setRequestType('released')}
                    />
                    <div className="type-text">
                      <div className="type-head">
                        <span className="type-name">Released</span>
                        <span className="type-badge">Spotify link required</span>
                      </div>
                      <div className="type-desc">
                        The track is already out and available on Spotify. <strong>Use a direct Spotify track link</strong>, or an album link only if that album is a <strong>1-track single</strong>.
                      </div>
                    </div>
                  </label>

                  {/* Planned */}
                  <label className={`type-item ${requestType === 'planned' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="request_type"
                      value="planned"
                      checked={requestType === 'planned'}
                      onChange={() => setRequestType('planned')}
                    />
                    <div className="type-text">
                      <div className="type-head">
                        <span className="type-name">Planned</span>
                        <span className="type-badge">Date required</span>
                      </div>
                      <div className="type-desc">
                        The release is coming soon and <strong>will be on Spotify</strong>. Provide
                        any link and/or attach an audio file. Select the <strong>planned release date</strong> below.
                      </div>
                      {requestType === 'planned' && (
                        <div className="planned-inline">
                          <label>Planned release date</label>
                          <input
                            type="date"
                            value={plannedDate}
                            min={todayISO()}
                            onChange={(e) => setPlannedDate(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  </label>

                  {/* Review only */}
                  <label className={`type-item ${requestType === 'review_only' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="request_type"
                      value="review_only"
                      checked={requestType === 'review_only'}
                      onChange={() => setRequestType('review_only')}
                    />
                    <div className="type-text">
                      <div className="type-head">
                        <span className="type-name">Review only</span>
                        <span className="type-badge warn">No playlist consideration</span>
                      </div>
                      <div className="type-desc">
                        This submission includes written feedback only, and the track is not evaluated for playlist fit. To request playlist consideration later, use an available credit for a new Planned submission once the release date is confirmed, or a new Released submission once the track is live.
                        Provide any link and/or attach an audio file.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

                  {/* Track link */}
                  <div className="form-row">
                    <label>Track Link</label>
                    <input
                      type="url"
                      value={track}
                      onChange={(e) => setTrack(e.target.value)}
                      placeholder={trackPlaceholder}
                    />
                    {linkErrorMessage && (
                      <div className={`form-error ${ATTN_RED}`}>
                        {linkErrorMessage}
                      </div>
                    )}
                    {isQuickCheckPlan && requestType !== 'review_only' && quickCheckWarning && (
                      <div className={`form-error ${ATTN_RED}`}>{quickCheckWarning}</div>
                    )}
                    {!isQuickCheckPlan && cooldownWarning && (
                      <div className={`form-error ${ATTN_RED}`}>{cooldownWarning}</div>
                    )}
                    {pendingDuplicateMessage && (
                      <div className={`form-error ${ATTN_RED}`}>{pendingDuplicateMessage}</div>
                    )}
                    {autofillMessage && (
                      <div className={`autofill-status ${autofillStatus === 'checking' ? 'autofill-status--pending' : 'autofill-status--warn'}`}>
                        {autofillMessage}
                      </div>
                    )}
                  </div>

                  <div className="form-row">
                    <label>Artist / Project Name</label>
                    <input
                      type="text"
                      required
                      value={artist}
                      onChange={(e) => { setArtist(e.target.value); setArtistDirty(true); }}
                      placeholder="Your artist name"
                      disabled={autofillLocked}
                    />
                  </div>

                  <div className="form-row">
                    <label>Song Name</label>
                    <input
                      type="text"
                      required
                      value={songName}
                      onChange={(e) => { setSongName(e.target.value); setSongDirty(true); }}
                      placeholder="Track title"
                      disabled={autofillLocked}
                    />
                  </div>

                  <div className="form-row">
                    <div className="thankyou-review-style">
                      <div className="thankyou-review-style__head">
                        <span className="thankyou-review-style__eyebrow">Review style</span>
                        <span className="thankyou-review-style__hint">Choose how direct you want the written review to be.</span>
                      </div>
                      <div className="thankyou-review-style__options" role="radiogroup" aria-label="Review style">
                        {REVIEW_STYLE_OPTIONS.map((option) => {
                          const premiumOnly = option.value === 'no_filter';
                          const disabled = isQuickCheckPlan || (premiumOnly && planNormalized !== 'premium');
                          const active = reviewStyleDisplayValue === option.value;
                          return (
                            <label
                              key={option.value}
                              className={`thankyou-review-option ${option.toneClass} ${active ? 'active' : ''} ${disabled ? 'is-disabled' : ''}`}
                              aria-disabled={disabled ? 'true' : 'false'}
                            >
                              <input
                                type="radio"
                                name="review_style"
                                value={option.value}
                                checked={active}
                                disabled={disabled}
                                onChange={() => handleReviewStyleChange(option.value)}
                              />
                              <span className="thankyou-review-option__box" aria-hidden="true" />
                              <span className="thankyou-review-option__body">
                                <span className="thankyou-review-option__title">
                                  {option.label}
                                  {premiumOnly && planNormalized !== 'premium' && (
                                    <span className="thankyou-review-option__badge">Premium only</span>
                                  )}
                                </span>
                                <span className="thankyou-review-option__desc">{option.description}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {isQuickCheckPlan && (
                        <div className="thankyou-review-style__lock">
                          Quick Check includes a brief written review. Review-style options are available with Standard and Premium.
                        </div>
                      )}
                      {!isQuickCheckPlan && (
                        <div className="thankyou-review-style__note">
                          {requestType === 'review_only'
                            ? 'This only affects the tone and depth of written feedback. Review only does not include playlist consideration.'
                            : 'This only affects the tone and depth of written feedback. Playlist consideration stays based on fit and curation standards.'}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <label>Notes (optional)</label>
                    <textarea
                      ref={notesRef}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Got a note, vibe, or thought to share? Drop it here."
                      rows={4}
                      maxLength={SUBMISSION_NOTES_MAX}
                    />
                    {notesLimitMessage && (
                      <div className={`form-error ${ATTN_BLUE}`}>
                        {notesLimitMessage}
                      </div>
                    )}
                    {notesErrorMessage && (
                      <div className={`form-error ${ATTN_RED}`}>
                        {notesErrorMessage}
                      </div>
                    )}
                  </div>

                  <div className="form-row file-row">
                    <label>Attach File (optional)</label>
                    {!file ? (
                      <input
                        type="file"
                        accept={SUBMISSION_AUDIO_ACCEPT}
                        onChange={handleFileChange}
                      />
                    ) : (
                      <div className="attached-file">
                        <span>{file.name}</span>
                        <button
                          type="button"
                          className="remove-file"
                          aria-label="Remove file"
                          title="Remove file"
                          onClick={() => {
                            setFile(null);
                            setFileErrorMessage('');
                          }}
                        >
                          ×
                        </button>
                      </div>
                    )}
                    <div className="form-hint">{SUBMISSION_AUDIO_HINT}</div>
                    {fileErrorMessage && (
                      <div className={`form-error ${ATTN_RED}`}>{fileErrorMessage}</div>
                    )}
                  </div>

                  <OrderVerificationLoader state={orderVerificationState} showCaption={false} />

                  <div className="form-row checkbox-row">
                    <label>
                      <input
                        type="checkbox"
                        checked={agree}
                        onChange={(e) => setAgree(e.target.checked)}
                      />{' '}
                      I own/represent the rights to submit this track for review.
                    </label>
                  </div>

                  {errorMsg && <div className={`form-error ${ATTN_RED}`}>{errorMsg}</div>}

                  <div
                    className="form-submit-row"
                    onClick={(e) => {
                      if (!canSubmit) {
                        e.preventDefault();
                        showWhyCantSubmit();
                      }
                    }}
                  >
                    {!canSubmit && (
                      <div className="submit-status ivg-attn ivg-brackets ivg-burst ivg-c-red ivg-block">
                        {submitBlockReason || 'Complete the required fields above.'}
                      </div>
                    )}
                    <button
                      type="submit"
                      className={`submit-btn ${canSubmit ? '' : 'inactive'}`}
                      disabled={!canSubmit || submitting}
                    >
                      {submitting ? 'Sending…' : 'Submit Track'}
                    </button>
                    {submitting && (
                      <InlineProgress message={submitProgressMessage} note={submitProgressNote} />
                    )}
                    </div>
                </>
              )}
            </form>
          )}
        </div>

        <a href="/">
          <img id="background-gif" src={require('./logo_art/logo_back.gif')} alt="Iva Gonzo logo visual" />
        </a>
      </div>

      <footer id="social-icons">
        <a href="/" aria-label="Back to home">
          <img className="social-icon" src={require('./soc icons/0_home.svg').default} alt="Home" />
        </a>
        {!hideInstagram && (
          <a href="https://www.instagram.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
            <img className="social-icon" src={require('./soc icons/1_icons8-instagram.svg').default} alt="Instagram" />
          </a>
        )}
        <a href="https://open.spotify.com/user/11185646448?si=e1a94395b1054a48" target="_blank" rel="noopener noreferrer">
          <img className="social-icon" src={require('./soc icons/2_icons8-spotify.svg').default} alt="Spotify" />
        </a>
        <a href="https://youtube.com/@ivagonzo/" target="_blank" rel="noopener noreferrer">
          <img className="social-icon" src={require('./soc icons/3_icons8-youtube.svg').default} alt="YouTube" />
        </a>
        {/*<a href="https://www.tiktok.com/@ivagonzo/" target="_blank" rel="noopener noreferrer">
          <img className="social-icon" src={require('./soc icons/4_icons8-tiktok.svg').default} alt="TikTok" />
        </a>*/}
        <a href="https://www.pinterest.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
          <img className="social-icon" src={require('./soc icons/5_icons8-pinterest.svg').default} alt="Pinterest" />
        </a>
        {/*<a href="https://x.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
          <img className="social-icon" src={require('./soc icons/6_icons8-twitterx.svg').default} alt="TwitterX" />
        </a>*/}
        <a href="mailto:support@ivagonzo.com" aria-label="Email">
          <img
            className="social-icon"
            src={require('./soc icons/7_email.svg').default}
            alt="Email"
          />
        </a>
      </footer>

      <div id="legal-links" className="legal-links">
        <button className="legal-link" onClick={() => setShowPrivacy(true)}>Privacy Policy</button>
        <button className="legal-link" onClick={() => setShowTerms(true)}>Terms &amp; Conditions</button>
        <button className="legal-link" onClick={() => setShowRefund(true)}>Refund Policy</button>
      </div>

      {showPrivacy && (
        <Modal title="Privacy Policy" onClose={() => setShowPrivacy(false)}>
          <PrivacyPolicy />
        </Modal>
      )}
      {showTerms && (
        <Modal title="Terms & Conditions" onClose={() => setShowTerms(false)}>
          <Terms />
        </Modal>
      )}
      {showRefund && (
        <Modal title="Refund Policy" onClose={() => setShowRefund(false)}>
          <RefundPolicy />
        </Modal>
      )}
    </div>
  );
}

function TypewriterText({ segments = [], animate = false, animationKey = 'default', className }) {
  const normalizedSegments = useMemo(
    () => segments.map((seg) => ({
      text: (seg?.text ?? '').toString(),
      href: seg?.href || null,
      target: seg?.target,
      rel: seg?.rel,
    })),
    [segments],
  );

  const { offsets, totalLength, flatText } = useMemo(() => {
    const offsets = [];
    let acc = 0;
    const texts = [];
    normalizedSegments.forEach((seg, idx) => {
      offsets[idx] = acc;
      acc += seg.text.length;
      texts.push(seg.text);
    });
    return { offsets, totalLength: acc, flatText: texts.join('') };
  }, [normalizedSegments]);

  const [visibleCount, setVisibleCount] = useState(animate ? 0 : totalLength);

  useEffect(() => {
    if (!animate) {
      setVisibleCount(totalLength);
      return;
    }
    if (totalLength === 0) {
      setVisibleCount(0);
      return;
    }
    setVisibleCount(0);
    let current = 0;
    let timer = null;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      current += 1;
      setVisibleCount((prev) => {
        const nextValue = Math.min(current, totalLength);
        return nextValue >= prev ? nextValue : prev;
      });
      if (current < totalLength) {
        const delay = 25 + Math.random() * 40;
        timer = setTimeout(tick, delay);
      }
    };

    timer = setTimeout(tick, 40);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [animate, totalLength, animationKey]);

  return (
    <span className={`${className || ''} typewriter-container`.trim()} aria-live="polite">
      <span className="typewriter-visible">
        {normalizedSegments.map((seg, idx) => {
          const start = offsets[idx] || 0;
          const available = Math.max(0, Math.min(seg.text.length, visibleCount - start));
          const content = available > 0 ? seg.text.slice(0, available) : '';
          if (!content) {
            return <span key={idx} />;
          }
          if (seg.href) {
            return (
              <a key={idx} href={seg.href} target={seg.target || '_blank'} rel={seg.rel || 'noreferrer'}>
                {content}
              </a>
            );
          }
          return <span key={idx}>{content}</span>;
        })}
      </span>
      <span className="typewriter-ghost" aria-hidden="true">{flatText}</span>
    </span>
  );
}

/* ================= Modal + legal ================= */

const Modal = ({ title, children, onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal" onClick={(e) => e.stopPropagation()}>
      <button className="close-button" onClick={onClose}>×</button>
      <h2>{title}</h2>
      {children}
    </div>
  </div>
);

// =====================
// PRIVACY POLICY
// =====================

const PrivacyPolicy = () => (
  <article className="legal-text">
    <p><strong>Last updated:</strong> 16 December 2025</p>
    <p>
      <strong>Who we are.</strong> This service is operated by <strong>Iva Gonzo</strong> (independent music curator).
      <strong> Data controller (where applicable):</strong> Iva Gonzo. For privacy questions or requests, contact{' '}
      <a href="mailto:support@ivagonzo.com">support@ivagonzo.com</a>.
    </p>
    <p>
      <strong>What we collect.</strong> Your email, artist/stage name, track links, optional notes, and any audio files you upload for review.
      If you request a payment-availability notification, we also collect the email address you submit for that purpose and the type of request indicated by the page on which you requested it (track submissions or support).
      We may also process basic technical data and limited purchase references received from the payment provider (such as order/payment IDs, purchase date/time,
      payment status, amount, currency, device/browser info, and IP address) for security, fraud prevention, troubleshooting, and record-keeping.
      We do not store your card or banking details.
    </p>
    <p>
      <strong>Legal basis (where applicable).</strong> We process personal data to perform our contract with you (deliver the service you purchased),
      to comply with legal obligations, and for legitimate interests such as security and fraud prevention. If you request a payment-availability notification,
      we use your email to provide that requested update.
    </p>
    <p>
      <strong>How we use it.</strong> Solely to deliver the feedback service you purchased (review your track, communicate with you, manage submissions,
      process refunds and manage prepaid reviews, prevent fraud/abuse, and keep required business records), or, if you request it, to confirm your signup and send a payment-availability update.
      We do not sell your personal data.
    </p>
    <p>
      <strong>Payments.</strong> Payments are processed by the payment provider shown at checkout. Card and billing data are processed by that provider
      and its payment partners under their own terms and privacy policies. We receive only limited transaction information and purchase references
      (for example, order/payment IDs, payment status, amount, and currency) needed to deliver the service, manage prepaid reviews, send receipts, prevent fraud,
      and resolve disputes. We do not store or have access to your full card or banking details.
    </p>
    <p>
      <strong>Service providers.</strong> To run the website and deliver the service, we use trusted providers (for example,
      third-party checkout and payment providers, hosting/storage, and email/support tools). We share only the minimum
      data needed to provide their services to us. We require (or contractually expect) them to use it only to provide services
      to us and to protect it appropriately. We do not sell your data. We may also disclose information if required by law.
    </p>
    <p>
      <strong>Advertising and measurement.</strong> Public pages may use <strong>Meta Pixel</strong> to measure advertising performance and build
      retargeting audiences based on non-sensitive interactions with public pages. We also use first-party analytics to understand
      page visits, referral sources, and the submission funnel. Meta may process technical data such as IP address,
      browser/device information, event timestamps, and event context under its own
      terms. We do not send uploaded audio files, private track links, submission notes, or full payment card/banking details to Meta through this pixel.
    </p>
    <p>
      <strong>Audio & private links.</strong> Uploaded audio files and private links (SoundCloud private URLs, unlisted YouTube links, etc.) are treated as confidential
      and are never published or shared. You are responsible for maintaining access to any private links you provide during the service (permissions/passwords/availability).
    </p>
    <p>
      <strong>Retention.</strong> Uploaded audio files are automatically deleted after 30 days of submission (test mode may shorten this to 10 minutes).
      Other data (e.g., emails, order records, support messages) may be retained as long as needed to provide the service, resolve disputes/chargebacks,
      prevent fraud, and comply with legal obligations.
    </p>
    <p>
      <strong>Your rights.</strong> You can request access, correction or deletion of your data by emailing <a href="mailto:support@ivagonzo.com">support@ivagonzo.com</a>.
      If you want us to delete your uploaded audio earlier, just ask. Where applicable, you may also have the right to object to processing and to lodge a complaint
      with your local data protection authority.
    </p>
    <p>
      <strong>Security.</strong> We use reasonable safeguards to protect your data and access to submissions. However, no online service can be guaranteed to be completely secure.
    </p>
    <p>
      <strong>Transfers & storage.</strong> Data may be stored/processed on servers located in the EU/US or other countries where our providers operate.
      Appropriate technical and organizational safeguards are applied.
    </p>
    <p>
      <strong>Cookies.</strong> We use essential cookies and similar technologies required for basic website functionality and security. Public
      pages may also use Meta Pixel and first-party analytics for advertising measurement, retargeting, and site performance analysis as described above.
    </p>
    <p><strong>Changes.</strong> Updates will be posted here. Continued use after changes means you accept the updated policy.</p>
  </article>
);


// =====================
// TERMS & CONDITIONS
// =====================

const Terms = () => (
  <article className="legal-text">
    <p>
      <strong>1. Service.</strong> You are purchasing a digital music <strong>review and consideration</strong> service. Approval and playlist placement are <strong>not guaranteed</strong>.
      If approved, sharing/placement may be done on one or more curator playlists on platforms such as <strong>Spotify, Apple Music, and YouTube Music</strong> (where technically possible).
      Guaranteed paid playlist placement or guaranteed stream promises would conflict with platform rules, including Spotify platform rules, so this product is sold as review and consideration only.
      We are an independent curator and are not affiliated with Spotify, Apple, YouTube, or any other third-party platform. Unless explicitly agreed otherwise, this service is provided in English.
      Feedback, review comments, submission-related updates, and support communication are delivered in English.
    </p>
    <p>
      <strong>2. Rights & permissions.</strong> By submitting any audio file or providing a link, you confirm you own the rights or have explicit permission from all rightsholders
      to share it with us for listening and review under these terms. Uploaded files may be temporarily stored for up to 30 days (or shorter in test mode).
      Links are not re-hosted; we may store the URL only as needed to perform the service and keep required business records.
    </p>
    <p>
      <strong>3. Payment.</strong> Prices are in USD. Payments are processed by the payment provider shown at checkout.
      We do not store or have access to your full card or banking details. You receive an order confirmation and submission link immediately after purchase.
    </p>
    <p>
      <strong>3a. Prepaid Reviews.</strong> Each prepaid review covers one track submission for the selected Iva Gonzo review service.
      <strong> Paid reviews remain available until used.</strong> <strong>Complimentary, bonus, courtesy, or reactivated reviews are not money</strong> and cannot be redeemed for cash or a cash refund.
    </p>
    <p>
      <strong>4. Eligibility.</strong> You must be at least 18 years old (or the age of legal majority in your country) to purchase and use this service.
    </p>
    <p>
      <strong>5. Submission materials &amp; access.</strong> You must provide either (a) one playable audio file, or (b) one playable third-party link. If you provide a link, you are
      responsible for ensuring we have access (permissions/password if needed) and that it remains available until the service is completed. If the file is corrupted/unsupported,
      or the link is incorrect, unavailable, geo-restricted, or access is not granted, we will request an updated file/link. Delivery timeframes start (and resume) only after we
      receive a <strong>valid, accessible</strong> track. If you do not provide a valid submission within <strong>14 days</strong> of our request, we may close the submission as incomplete after notifying you;
      in such case, you may need to purchase/submit again, and refunds/overdue remedies do not apply. <strong>We start working</strong> when we first access the submitted file/link and begin listening/reviewing.
    </p>
    <p>
      <strong>6. Review delivery time.</strong> Review timeframes are counted from the moment we receive a valid submission (a playable file or accessible link).
      Quick Check: up to 5 days. Standard: up to 5 days. Premium: up to 48 hours. If we miss a promised review timeframe,
      your remedies are described in the Refund Policy.
    </p>
    <p>
      <strong>7. Outcome.</strong> “Approved” = your track enters our placement queue and may be shared on one or more playlists (where technically possible). “Reviewed” = you still receive feedback, but no placement.
      Review only includes written feedback but no playlist decision. Any later playlist consideration requires a separate Planned or Released submission and an available review credit.
      For Released and Planned submissions, Quick Check includes a brief written review and an independent curator decision. Standard and Premium include written feedback. The curator’s decision is final and subjective. We do not guarantee any results
      such as streams, saves, followers, algorithmic boosts, charting, press coverage, or career outcomes. Platform rules, including Spotify platform rules, are one reason we do not sell guaranteed placement or guaranteed stream outcomes.
    </p>
    <p>
      <strong>8. Placement.</strong> Placement applies to <strong>approved submissions only</strong> and, where it occurs, may be made on one or more curator playlists where technically possible.
      Placement is not guaranteed and is not a separately purchased service outcome.
    </p>
    <p>
      <strong>9. Track availability (platform/artist removal).</strong> If a track becomes unavailable on a platform (for example, removed by the artist/label/distributor, made private, geo-restricted, taken down, or otherwise inaccessible),
      this is outside our control. In such cases, we are not responsible for interruptions and it does not create a right to refunds or replacement reviews.
    </p>
    <p>
      <strong>10. Intellectual property.</strong> All rights to your music remain yours. Written feedback is © Iva Gonzo; you may quote it for personal/promo use.
      You grant us a limited license to access, stream, and temporarily store your submission solely to deliver the service.
    </p>
    <p>
      <strong>11. Liability.</strong> Our maximum liability is limited to the amount you paid for the affected submission. To the maximum extent permitted by law, we are not liable for indirect, incidental, special,
      consequential, or punitive damages (including lost profits/opportunities), even if advised of the possibility.
    </p>
    <p>
      <strong>12. Chargebacks.</strong> Please contact us first — we resolve issues quickly. If needed, use the dispute process offered by your payment provider or card issuer within its applicable deadlines.
      Unjustified disputes or chargebacks may be formally contested using order logs, payment records, submission timestamps, portal activity,
      delivery evidence, support communications, and relevant platform records. Proven abuse or fraudulent disputes may lead to restrictions or a ban on future purchases.
    </p>
    <p>
      <strong>13. Changes.</strong> New terms apply to new purchases only.
    </p>
    <p>
      <strong>14. Re-submissions.</strong> Tracks that were previously approved/placed (status “Shared”) cannot be re-submitted as <em>Quick Check</em>. Use Standard or Premium instead.
    </p>
    <p>
      <strong>15. Prohibited content & abuse.</strong> You must not submit illegal content, content you don’t have rights to share, malware, deceptive metadata, spam, or harassment.
      We may refuse, suspend, or stop providing the service (and/or restrict future purchases) in cases of abuse, fraud, harassment, or suspected rights violations.
    </p>
    <p>
      <strong>16. Right to refuse service.</strong> We may, <strong>as permitted by law</strong>, refuse, suspend, or cancel any order/submission at our discretion (for example,
      suspected fraud/abuse/harassment, rights or platform compliance concerns, technical impossibility, or safety reasons). We may not be able
      to provide detailed reasons. If we cancel <strong>before</strong> we start working on your submission (before listening/review), you will receive a
      <strong> full refund</strong> (or, for a multi-review purchase, the unused prepaid review will be restored). If we cancel due to your breach of these terms
      (e.g., rights violations, abusive behavior, or fraudulent activity), refunds or replacement reviews may be denied to the maximum extent permitted by law.
    </p>
    <p>
      <strong>17. Third-party services.</strong> We are not responsible for outages, takedowns, restrictions, policy enforcement, or changes by third-party platforms (e.g., Spotify/YouTube/SoundCloud).
      This includes platform rules that may restrict or prohibit guaranteed paid placement, stream manipulation, or similar conduct.
      Links to third-party services are provided/used for convenience and remain governed by their own terms.
    </p>
    <p>
      <strong>18. Disclaimer.</strong> The service is provided on an “as is” and “as available” basis to the maximum extent permitted by law. We do not make warranties that the service will be uninterrupted,
      error-free, or meet every expectation.
    </p>
    <p>
      <strong>19. Indemnity.</strong> You agree to indemnify and hold us harmless from claims, damages, losses, and expenses (including reasonable legal fees) arising out of your submission, your breach of these terms,
      or any allegation that you lacked rights/permissions for the content you provided.
    </p>
    <p>
      <strong>20. Force majeure &amp; maintenance.</strong> We are not responsible for delays or failure caused by events beyond our reasonable control (e.g., outages, infrastructure failures, platform downtime, force majeure).
      We may temporarily suspend access for maintenance/security.
    </p>
    <p>
      <strong>21. Disputes.</strong> If you have an issue, contact us first at{' '}
      <a href="mailto:support@ivagonzo.com">support@ivagonzo.com</a> and we’ll try to resolve it informally.
      If needed, you may also use the official refund or dispute process offered by your payment provider or card issuer, within its applicable deadlines.
      Contacting support first does not replace or extend those deadlines.
      Nothing in these terms limits your mandatory consumer rights where applicable.
    </p>
    <p>
      <strong>22. Severability.</strong> If any part of these terms is found invalid or unenforceable, the remaining parts stay in effect.
    </p>
    <p>
      <strong>23. Electronic communications.</strong> By using the service, you agree that notices and communications may be provided electronically (e.g., by email).
    </p>
    <p><strong>Contact:</strong> <a href="mailto:support@ivagonzo.com">support@ivagonzo.com</a></p>
  </article>
);


// =====================
// REFUND POLICY
// =====================

const RefundPolicy = () => (
  <article className="legal-text">
    <p>
      <strong>1. Return period.</strong> You may request a refund within the return/withdrawal period shown on the checkout page and/or in your receipt email
      (this period is at least <strong>14 days</strong> where applicable and may be longer depending on applicable law and country). All refunds are processed via the original payment method.
    </p>
    <p>
      <strong>2. Before you submit your track.</strong> If you have <strong>not submitted your track (file or link) yet</strong>, you can cancel your order within the return/withdrawal period described in section 1
      and receive a <strong>full refund</strong>.
    </p>
    <p>
      <strong>3. Start of service during the withdrawal period (where applicable).</strong> Where consumer withdrawal/cancellation rights apply, by submitting your track you request that we begin performing the service immediately.
      You acknowledge that, to the extent permitted by law, once the <strong>review outcome</strong> has been delivered, the service is considered performed and your right of withdrawal may no longer apply.
    </p>
    <div>
      <p>
        <strong>4. After you submit your track &amp; review outcome is delivered.</strong> Once we have listened to your track and delivered the <strong>review outcome</strong>, refunds are not granted,
        because the review/consideration service has been provided. For Released and Planned <strong>Quick Check</strong> submissions, the “review outcome” includes a brief written review and an independent curator decision. A Review only outcome does not include a playlist decision. For <strong>Standard/Premium</strong>,
        the outcome includes written feedback. Approval and playlist placement are not guaranteed.
      </p>
      <blockquote>
        <p><strong>Important:</strong> Approval and playlist placement are not guaranteed.</p>
        <p>
          You are paying for a professional review and consideration, not for guaranteed playlist placement. A refund is not possible just because your track was not added to a playlist.
          Guaranteed paid placement or guaranteed stream promises would conflict with platform rules, including Spotify platform rules.
        </p>
      </blockquote>
    </div>
    <div>
      <p><strong>5. Multi-review purchases.</strong></p>
      <ul>
        <li>A full refund within the return/withdrawal period described in section 1 is possible only if no prepaid reviews from the purchase were used.</li>
        <li>As soon as any prepaid review from the purchase is used, cash refunds for the whole purchase are no longer available, <strong>except where required by law</strong>.</li>
        <li><strong>Complimentary/bonus/courtesy/reactivated reviews.</strong> Any complimentary, bonus, courtesy, or reactivated reviews are not money and are not eligible for cash refunds or redemption.</li>
        <li>
          If a single submission from a multi-review purchase is refunded <strong>where required by law</strong> or at <strong>our sole discretion</strong>, the refundable amount is calculated <strong>pro-rata</strong>:
        </li>
      </ul>
      <blockquote>
        <p>refund per track = total price of the multi-review purchase ÷ number of prepaid reviews in that purchase.</p>
        <p>The remaining unused paid reviews remain available for future submissions.</p>
      </blockquote>
    </div>
    <div>
      <p>
        <strong>6. Missed review deadline.</strong> If we fail to deliver your review within the promised timeframe <strong>despite having a valid submission</strong>,
        the terms below apply:
      </p>

      <ul>
        <li>
          <strong>Review deadlines (counted from a valid submission):</strong> Quick Check &amp; Standard: up to 5 days. Premium: up to 48 hours.
        </li>
      </ul>

      <p>
        <strong>Pauses/exclusions.</strong> Deadlines are paused (and overdue remedies do not apply) while we do not have a valid submission, including cases of inaccessible/incorrect links,
        missing permissions/passwords, corrupted/unsupported files, geo-restrictions, or when the track is removed/made private/taken down before review is complete.
        We are also not responsible for delays caused by third-party platform outages or restrictions beyond our control.
      </p>
      <p>
        <strong>Abandoned/incomplete submissions.</strong> If we request an updated file/link and you do not provide a valid, accessible submission within <strong>14 days</strong>,
        we may close the submission as incomplete. In that case, missed-deadline remedies do not apply.
      </p>
      <p>
        If we miss a promised review timeframe <strong>despite having a valid submission</strong>, our standard remedy is to
        <strong> re-activate the overdue submission</strong> and add <strong>one additional review of the same type</strong> to your account as compensation (if applicable).
      </p>
      <p>
        This <strong>restore + complimentary review</strong> remedy is the standard resolution for missed review deadlines. Monetary refunds for missed review deadlines are
        <strong> not offered as a standard client-selected option</strong>.
      </p>
      <p>
        A monetary refund for a missed review deadline may be granted only <strong>where required by law</strong> or at <strong>our sole discretion</strong>.
        For multi-review purchases, any such refund for an affected submission is calculated <strong>pro-rata</strong> under section 5, while the remaining unused paid reviews stay active.
      </p>
    </div>
    <p>
      <strong>7. Technical issues.</strong> If there is a technical problem on our side (file upload error, server issue, etc.), we will help you re-submit your track,
      restore the affected submission, or otherwise correct the issue. A monetary refund may be granted only <strong>where required by law</strong> or at <strong>our sole discretion</strong>.
    </p>
    <div>
      <p>
        <strong>8. How to request a refund.</strong> Please contact{' '}
        <a href="mailto:support@ivagonzo.com">support@ivagonzo.com</a> first with the email used for payment and, if available, your <strong>Order ID</strong> if you need help.
        If needed, you may also use the official refund or dispute process offered by your payment provider or card issuer, within its applicable deadlines.
        Contacting support first does not replace or extend those deadlines. Any request for a monetary refund is reviewed under this policy and, when approved, is processed via the original payment method.
      </p>
      <p>
        <strong>9. Refund execution.</strong> Refunds are issued using the original payment method. After a refund is initiated,
        the time for the funds to appear in your account depends on your bank/payment provider.
        If you believe your payment method was used without authorization, contact your bank immediately.
      </p>
      <p>
        <strong>10. Orders cancelled by us.</strong> If we cancel an order/submission <strong>before</strong> we start working on it (before listening/review),
        you will receive a <strong>full refund</strong> (or, for a multi-review purchase, the unused prepaid review will be restored). If the cancellation is due to
        your breach of the Terms (e.g., rights violations, abusive behavior, fraud), refunds or replacement reviews may be denied to the maximum extent permitted by law.
      </p>
      <p>
        If your order includes a client portal or an “Overdue” feature, the portal may offer re-activation and complimentary-review remedies. It does not create an automatic right to a monetary refund.
      </p>
    </div>
    <p>
      <strong>11. Chargebacks.</strong> Please contact us first. If needed, use the dispute process offered by your payment provider or card issuer within its applicable deadlines.
      Unjustified disputes or chargebacks may be formally contested using order logs, payment records, submission timestamps, portal activity,
      delivery evidence, support communications, and relevant platform records. Proven abuse may lead to restrictions for future purchases.
    </p>
    <p>
      <strong>12. Statutory rights &amp; payment processor discretion.</strong> Nothing in this policy limits your mandatory consumer rights where applicable.
      In exceptional cases (e.g., suspected fraud or abuse), the payment processor may process a refund at its discretion.
    </p>
  </article>
);
